import * as THREE from 'three';
import {
  RENDER_DISTANCE, CHUNK_SIZE, WORLD_SEED, EYE_HEIGHT, TICK_DT, TERRAIN_BASE,
  GameMode, SKY_COLOR, NETHER_SKY_COLOR, UNDERWATER_COLOR,
  OVERWORLD_FOG_NEAR, FOG_FAR_FACTOR, NETHER_FOG_NEAR, NETHER_FOG_FAR,
  UNDERWATER_FOG_NEAR, UNDERWATER_FOG_FAR,
  CAMERA_FOV, CAMERA_NEAR, CAMERA_FAR_FACTOR,
  LAVA_DAMAGE, SOUL_SAND_SLOWDOWN,
  MAX_HOSTILE_MOBS, HOSTILE_SPAWN_INTERVAL, HOSTILE_DESPAWN_DISTANCE,
  HOSTILE_SPAWN_MIN_DIST, HOSTILE_SPAWN_RANGE,
  TELEPORT_MSG_DURATION, SMELT_DURATION, MAX_STACK_SIZE,
  SPRINT_FOV_BOOST,
  Difficulty, DIFFICULTY_CONFIG,
  END_SKY_COLOR, END_FOG_NEAR, END_FOG_FAR,
} from './constants.js';
import { generateAtlasTexture, BlockId } from './world/BlockType.js';
import { TerrainGenerator } from './world/TerrainGenerator.js';
import { World } from './world/World.js';
import { Player } from './player/Player.js';
import { FPSCamera } from './player/Camera.js';
import { PlayerController } from './player/PlayerController.js';
import { InputManager } from './engine/InputManager.js';
import { GameLoop } from './engine/GameLoop.js';
import { HUD } from './ui/HUD.js';
import { Hotbar } from './ui/Hotbar.js';
import { BlockHighlight } from './ui/BlockHighlight.js';
import { BlockInteraction } from './player/BlockInteraction.js';
import { Inventory } from './player/Inventory.js';
import { InventoryScreen } from './ui/InventoryScreen.js';
import { SoundManager } from './audio/SoundManager.js';
import { SaveManager } from './world/SaveManager.js';
import { EntityManager } from './entities/EntityManager.js';
import { Intro } from './ui/Intro.js';
import { Terminal } from './ui/Terminal.js';
// New systems
import { DayNight } from './world/DayNight.js';
import { Health } from './player/Health.js';
import { Combat } from './player/Combat.js';
import { BoatSystem } from './player/BoatSystem.js';
import { HealthBar } from './ui/HealthBar.js';
import { ItemDropManager } from './items/ItemDropManager.js';
import { CraftingSystem } from './items/CraftingSystem.js';
import { CraftingScreen } from './ui/CraftingScreen.js';
import {
  HOSTILE_MOB_TYPES,
  createZombieMesh, createSkeletonMesh, createCreeperMesh, createSpiderMesh,
  createEndermanMesh, createSlimeMesh,
  createZombiePiglinMesh, createGhastMesh, createBlazeMesh,
  createWitchMesh, createPhantomMesh, createDrownedMesh, createPillagerMesh,
  createFireballEntity, updateFireball,
  updateHostileAI, updateArrow, processCreeperExplosion
} from './entities/HostileMobs.js';
import { NetherGenerator } from './world/NetherGenerator.js';
import { EndGenerator } from './world/EndGenerator.js';
import { DimensionManager } from './world/DimensionManager.js';
import { activatePortal, checkPortalEntry, buildPortalFrame, checkEndPortalEntry, buildEndExitPortal, isEndPortalComplete, activateEndPortal } from './world/Portal.js';
import { VictoryScreen } from './ui/VictoryScreen.js';
import { RaidSystem } from './entities/RaidSystem.js';
import { PistonSystem } from './world/PistonSystem.js';
import { HopperSystem } from './world/HopperSystem.js';
import { XPSystem } from './player/XPSystem.js';
import { EnchantScreen } from './ui/EnchantScreen.js';
import { Weather } from './effects/Weather.js';
import { ParticleSystem } from './effects/Particles.js';
import { ChestScreen } from './ui/ChestScreen.js';
import { FurnaceScreen } from './ui/FurnaceScreen.js';
import { TileEntities } from './world/TileEntities.js';
import { FarmingSystem } from './world/FarmingSystem.js';
import { rollMobDrops, isEdible, getFoodValue, ItemId, MOB_XP } from './items/ItemRegistry.js';
import { tirarObjeto, admiteRareza, bonoDe, describir, colorDe } from './items/Rareza.js';
import { getFuelValue } from './items/CraftingSystem.js';
import { BowSystem } from './player/BowSystem.js';
import { TradingScreen, getVillagerTradeData } from './ui/TradingScreen.js';
import { Minimap } from './ui/Minimap.js';
import { AchievementSystem } from './systems/Achievements.js';
import { RecipeBook } from './ui/RecipeBook.js';
import { PauseMenu } from './ui/PauseMenu.js';
import { WaterPhysics } from './world/WaterPhysics.js';
import { Screenshot } from './ui/Screenshot.js';
import { LoadingScreen } from './ui/LoadingScreen.js';
import { PotionSystem, POTION_EFFECTS } from './items/PotionSystem.js';
import { PotionEffectDisplay } from './ui/PotionEffectDisplay.js';
import { BrewingScreen } from './ui/BrewingScreen.js';
import { AmbientMusic } from './audio/AmbientMusic.js';
import { ScreenEffects } from './ui/ScreenEffects.js';
import { GameModeMenu } from './ui/GameModeMenu.js';
import { ArmorSystem } from './player/ArmorSystem.js';
import { BedSystem } from './world/BedSystem.js';
import { GraveHideSystem } from './world/GraveHideSystem.js';
import { ItemDropSystem } from './systems/ItemDropSystem.js';
import { damageTool, isTool } from './items/ToolSystem.js';
import { incrementWeaponUses, getWeaponDamageMult, getWeaponLevelName } from './items/WeaponXP.js';
import { RedstoneSystem } from './world/RedstoneSystem.js';
import { BreedingSystem } from './entities/BreedingSystem.js';
import { InteractiveBlocks } from './world/InteractiveBlocks.js';
import { MapItem } from './items/MapItem.js';
import { FishingSystem } from './items/FishingSystem.js';
import { BossSystem } from './entities/BossSystem.js';
import { Tutorial } from './ui/Tutorial.js';
import { MainMenu } from './ui/MainMenu.js';
import { ModAPI } from './api/ModAPI.js';
import { ChatSystem } from './ui/ChatSystem.js';
import { updateEntityAnimation, triggerHurt, triggerDeath } from './entities/EntityAnimations.js';
import { WaterShader } from './effects/WaterShader.js';
import { CaveFog } from './effects/CaveFog.js';
import { PerformanceOverlay } from './ui/PerformanceOverlay.js';
import { ScreenShake } from './effects/ScreenShake.js';
import { P2PManager } from './network/P2PManager.js';
import { MultiplayerMenu } from './ui/MultiplayerMenu.js';
import { PhoneScreen } from './ui/PhoneScreen.js';
import { ComputerScreen } from './ui/ComputerScreen.js';
import { PostProcessing } from './effects/PostProcessing.js';
import { CloudSystem } from './effects/CloudSystem.js';
import { TouchControls } from './engine/TouchControls.js';
import { CloudSave } from './cloud/CloudSave.js';
import { StatsScreen } from './ui/StatsScreen.js';
import { SkinSelector } from './ui/SkinSelector.js';
import { HerreroScreen } from './ui/HerreroScreen.js';
import { ModoMazmorra } from './systems/ModoMazmorra.js';
import { codificar, decodificar, urlDeCodigo, codigoDeUrl, LIMITE_COMODO } from './world/CodigoMundo.js';
import { crearModelo, vestirModelo, animarModelo } from './player/skins/modelo.js';
import { DamageNumbers } from './ui/DamageNumbers.js';
import { QuestSystem } from './systems/QuestSystem.js';
import { SkillTree } from './player/SkillTree.js';
import { MagicSystem } from './player/MagicSystem.js';
import { DynamicEvents } from './systems/DynamicEvents.js';
import { ComboSystem } from './player/ComboSystem.js';
import { animations } from './ui/AnimationSystem.js';
import { notifications } from './ui/NotificationQueue.js';
import { themeSystem } from './ui/ThemeSystem.js';
import { achievementCelebration } from './ui/AchievementCelebration.js';
import { DamageDirection } from './ui/DamageDirection.js';
import { AccessibilityMenu } from './ui/AccessibilityMenu.js';
import { HUDConfig } from './ui/HUDConfig.js';
import { SeasonSystem } from './world/SeasonSystem.js';
import { ChallengeMode } from './systems/ChallengeMode.js';
import { SpeedrunTimer } from './ui/SpeedrunTimer.js';
import { eventBus, GameEvents } from './engine/EventBus.js';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';

// --- Hide game UI until intro finishes ---
const instructions = document.getElementById('instructions');
instructions.style.display = 'none';

// --- Main Menu + Intro Flow ---
let gameOptions = { newWorld: false };

const mainMenu = new MainMenu((options) => {
  gameOptions = options;
  mainMenu.hide();
  // Try intro video, if it fails/skips, start game directly
  const intro = new Intro(() => {
    instructions.style.display = 'block';
    initGame();
  });
});

/* Un enlace compartido entra DIRECTO, sin pasar por el menú de mundos.

   Si se dejara el menú, el visitante tendría que pulsar «Nuevo Mundo → Crear»
   —que borra una ranura— o meterse en una partida ajena para ver el mundo de
   Fede. Las dos cosas están mal: quien abre el enlace viene a mirar, no a
   gestionar ranuras, y desde luego no a que le borren nada.

   `visita: true` lo marca para que más abajo no se cargue ni se guarde ningún
   slot. */
if (codigoDeUrl()) {
  gameOptions = { newWorld: false, visita: true };
  mainMenu.hide();
  instructions.style.display = 'block';
  /* En un microtask, no aquí mismo: initGame() está izada pero usa `const` que
     se declaran más abajo en el módulo. Llamarla durante la evaluación revienta
     con «Cannot access ... before initialization». */
  queueMicrotask(() => { initGame(); });
} else {
  mainMenu.show();
}

// --- Game Initialization (runs after intro) ---
let _gameInitialized = false;
async function initGame() {
  if (_gameInitialized) return;
  _gameInitialized = true;

  // --- Brief Video Overlay Helper ---
  // Plays a short fullscreen video overlay that fades in/out. Used for momentary events.
  function playBriefVideo(src, duration = 2) {
    fetch(src, { method: 'HEAD' }).then(r => {
      if (!r.ok) return;
      const video = document.createElement('video');
      video.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;object-fit:cover;z-index:940;opacity:0;transition:opacity 0.3s;pointer-events:none;';
      video.src = src;
      video.muted = true;
      video.playsInline = true;
      document.body.appendChild(video);
      video.play().then(() => { video.style.opacity = '0.7'; }).catch(() => {});
      setTimeout(() => {
        video.style.opacity = '0';
        setTimeout(() => { video.remove(); }, 500);
      }, duration * 1000);
    }).catch(() => {});
  }

  // Debounced level-up video (avoid spamming if multiple XP sources hit simultaneously)
  let _lastLevelUpVideoTime = 0;
  function playLevelUpVideo() {
    const now = performance.now();
    if (now - _lastLevelUpVideoTime < 5000) return; // 5s cooldown
    _lastLevelUpVideoTime = now;
    playBriefVideo('/levelup.webm', 2);
  }

  // --- Scene Setup ---
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(SKY_COLOR);
  scene.fog = new THREE.Fog(SKY_COLOR, OVERWORLD_FOG_NEAR, RENDER_DISTANCE * CHUNK_SIZE * FOG_FAR_FACTOR);

  const camera = new THREE.PerspectiveCamera(
    CAMERA_FOV,
    window.innerWidth / window.innerHeight,
    CAMERA_NEAR,
    Math.max(RENDER_DISTANCE * CHUNK_SIZE * CAMERA_FAR_FACTOR, 800)
  );

  // --- WebGPU with WebGL 2 fallback ---
  let renderer;
  let usingWebGPU = false;
  try {
    const { WebGPURenderer } = await import('three/webgpu');
    renderer = new WebGPURenderer({ antialias: false });
    await renderer.init();
    usingWebGPU = true;
    console.log('[FedeWorld] WebGPU renderer initialized');
  } catch (e) {
    console.log('[FedeWorld] WebGPU not available, falling back to WebGL', e.message);
    renderer = new THREE.WebGLRenderer({ antialias: false });
  }
  renderer.setSize(window.innerWidth, window.innerHeight);
  /* Nunca window.devicePixelRatio a pelo: en Retina son 4x los píxeles. El
     valor real lo fija PauseMenu al construirse (tope 1,5) y este es solo el
     que rige durante los primeros cuadros. */
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.5));
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  document.body.appendChild(renderer.domElement);
  renderer.domElement.addEventListener('contextmenu', (e) => e.preventDefault());

  // Wrap requestPointerLock to swallow expected rejections (headless, security errors, etc.)
  // Without this, ~13 call sites can throw "The root document of this element is not valid for pointer lock."
  const _origRPL = renderer.domElement.requestPointerLock.bind(renderer.domElement);
  renderer.domElement.requestPointerLock = function() {
    try {
      const r = _origRPL();
      if (r && typeof r.catch === 'function') r.catch(() => {});
      return r;
    } catch (e) { /* swallow */ }
  };

  /* ── Bloom ─────────────────────────────────────────────────────────────
     Estaba SOLO en la ruta WebGL, con el comentario «WebGPU ya trae mapeo de
     tonos» — que no es lo mismo: el mapeo de tonos comprime el rango, el bloom
     desborda la luz. En un Mac, que va por WebGPU, no había ningún bloom.

     Se nota justo donde más falta hace ahora: las antorchas en una cueva y los
     haces de las legendarias, que sin bloom son un cilindro plano de color. */
  let composer = null;
  let postWebGPU = null;
  let brilloActivo = true;
  if (usingWebGPU) {
    try {
      const { PostProcessing } = await import('three/webgpu');
      const { pass } = await import('three/tsl');
      const { bloom } = await import('three/addons/tsl/display/BloomNode.js');
      const escena = pass(scene, camera);
      /* Umbral alto a propósito: solo desborda lo que de verdad emite —el
         botín, el fuego, la lava—, no el cielo entero. */
      const brillo = bloom(escena.getTextureNode(), 0.55, 0.6, 0.9);
      postWebGPU = new PostProcessing(renderer);
      postWebGPU.outputNode = escena.add(brillo);
      console.log('[FedeWorld] bloom WebGPU activo');
    } catch (e) {
      console.log('[FedeWorld] sin bloom en WebGPU:', e.message);
      postWebGPU = null;
    }
  }
  if (!usingWebGPU) {
    composer = new EffectComposer(renderer);
    const renderPass = new RenderPass(scene, camera);
    composer.addPass(renderPass);

    const bloomPass = new UnrealBloomPass(
      new THREE.Vector2(window.innerWidth, window.innerHeight),
      0.3,    // strength: subtle
      0.8,    // radius: medium spread
      0.85    // threshold: only bright blocks glow
    );
    composer.addPass(bloomPass);

    const outputPass = new OutputPass();
    composer.addPass(outputPass);
  }

  /* ── Luz ──────────────────────────────────────────────────────────────
     Antes: ambiente 0,6 + direccional 0,8. Con tanto relleno ambiental una
     sombra proyectada apenas baja el brillo un 40% y no se ve: se activaron
     las sombras y el paisaje seguía igual de plano.

     Ahora el ambiente baja a 0,32 y el sol sube a 1,15. La suma da un brillo
     parecido en las caras iluminadas, pero la diferencia entre estar al sol y
     estar a la sombra pasa de casi nada a la mitad. Ahí es donde se ve.

     El ambiente es azulado, no blanco: es luz de cielo rebotada, y darle
     temperatura hace que las sombras tiren a azul en vez de a gris sucio. */
  const ambientLight = new THREE.HemisphereLight(0xBFD8F5, 0x4A4438, 0.32);
  scene.add(ambientLight);
  const directionalLight = new THREE.DirectionalLight(0xFFF6E0, 1.15);
  directionalLight.position.set(100, 200, 50);

  /* ── SOMBRAS ──────────────────────────────────────────────────────────
     El juego no tenía ninguna. Un mundo de cubos sin sombra proyectada se ve
     plano: no se sabe qué está encima de qué, ni a qué altura flota un árbol.

     Se puede permitir porque está medido: en un M3 el tiempo por cuadro es de
     8,1 ms y NO se mueve aunque los píxeles bajen siete veces (ver
     scripts/medir-fps.mjs). La GPU estaba ociosa; esto es gastar ese hueco en
     algo que se ve.

     El mapa de sombras SIGUE al jugador —una caja de 80 bloques alrededor— en
     vez de cubrir el mundo: a 4.096 px de textura, cubrir todo daría sombras
     de un pixelado ridículo, y cubrir 80 bloques da un borde limpio donde
     Fede está mirando. */
  const SOMBRA = { off: 0, bajo: 1024, alto: 2048 };
  let calidadSombra = 'alto';
  directionalLight.castShadow = true;
  directionalLight.shadow.mapSize.set(SOMBRA.alto, SOMBRA.alto);
  const cs = directionalLight.shadow.camera;
  cs.left = -40; cs.right = 40; cs.top = 40; cs.bottom = -40;
  cs.near = 1; cs.far = 400;
  /* Sin este sesgo salen las «acne stripes»: el suelo se sombrea a sí mismo y
     aparece un rayado que se ve horrible en superficies planas grandes. */
  directionalLight.shadow.bias = -0.0012;
  directionalLight.shadow.normalBias = 0.03;
  scene.add(directionalLight.target);
  scene.add(directionalLight);

  // --- Texture Atlas ---
  const atlasTexture = generateAtlasTexture();

  // --- Initialize Core Systems ---
  const inputManager = new InputManager();
  const soundManager = new SoundManager();
  const worldSlot = gameOptions.slot !== undefined ? gameOptions.slot : 0;
  const saveManager = new SaveManager(worldSlot);
  const terrainGenerator = new TerrainGenerator(WORLD_SEED);
  const world = new World(scene, terrainGenerator, atlasTexture, saveManager);
  world.setCamera(camera);
  const player = new Player();
  const inventory = new Inventory();
  const inventoryScreen = new InventoryScreen(inventory);
  inventoryScreen.setSoundManager(soundManager);
  const fpsCamera = new FPSCamera(camera, renderer.domElement);
  const playerController = new PlayerController(player, fpsCamera, inputManager);
  const hud = new HUD();
  const hotbar = new Hotbar(inventory);
  const perfOverlay = new PerformanceOverlay();
  const blockHighlight = new BlockHighlight(scene);
  const blockInteraction = new BlockInteraction(
    world, fpsCamera, inputManager, blockHighlight, player, inventory, soundManager
  );
  const entityManager = new EntityManager(scene, world, soundManager);

  // --- Cuerpo del jugador (tercera persona, con la skin elegida) ---
  //
  // Antes esto era un muñeco con los colores escritos a mano —torso cian,
  // cabeza tostada— y el selector de skins no lo tocaba: elegir una skin no
  // cambiaba nada. Ahora lleva la textura de 64×64 que corresponda.
  let tpGroup = null;
  let _pasoFase = 0;

  // --- Nether Dimension ---
  const netherGenerator = new NetherGenerator(WORLD_SEED);
  const endGenerator = new EndGenerator(WORLD_SEED);
  const victoryScreen = new VictoryScreen();
  const raidSystem = new RaidSystem();
  raidSystem.onShowMessage = (msg) => hud.showMessage(msg);
  raidSystem.onPlayVideo = (src, dur) => playBriefVideo(src, dur);
  raidSystem.onSpawnMobs = (type, count, center) => {
    let spawned = 0;
    const builders = { PILLAGER: createPillagerMesh, WITCH: createWitchMesh };
    const stats = HOSTILE_MOB_TYPES[type];
    if (!stats || !builders[type]) return 0;
    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2;
      const dist = 15 + Math.random() * 20;
      const wx = Math.floor(center.x + Math.cos(angle) * dist);
      const wz = Math.floor(center.z + Math.sin(angle) * dist);
      const wy = world.getTopBlock(wx, wz);
      if (wy <= 0) continue;
      const { mesh, refs } = builders[type]();
      mesh.position.set(wx + 0.5, wy + 1, wz + 0.5);
      scene.add(mesh);
      const mob = {
        type, mesh, refs,
        position: new THREE.Vector3(wx + 0.5, wy + 1, wz + 0.5),
        velocity: new THREE.Vector3(0, 0, 0),
        health: stats.health, maxHealth: stats.health,
        aiState: 'idle', aiTimer: 0, wanderDir: new THREE.Vector3(),
        attackCooldown: 0, fuseTimer: 0, fusing: false, sunBurnTimer: 0,
        idleTimer: 0, wanderTimer: 1 + Math.random() * 3,
        isRaidMob: true,
      };
      if (type === 'PILLAGER' && Math.random() < 0.1) mob.isCaptain = true;
      hostileMobs.push(mob);
      spawned++;
    }
    return spawned;
  };
  const dimensionManager = new DimensionManager();

  // --- New Systems ---
  const dayNight = new DayNight(scene, directionalLight, ambientLight, scene.fog);
  const health = new Health();
  playerController.health = health;
  const combat = new Combat();
  const boatSystem = new BoatSystem();
  boatSystem.onPlayVideo = (src, dur) => playBriefVideo(src, dur);
  const healthBar = new HealthBar(health);
  const itemDropManager = new ItemDropManager(scene);
  const craftingSystem = new CraftingSystem();
  const craftingScreen = new CraftingScreen(inventory, craftingSystem);
  craftingScreen.setSoundManager(soundManager);

  // --- Weather & Particles ---
  const weather = new Weather(scene, dayNight);
  weather.setSoundManager(soundManager);
  const particles = new ParticleSystem(scene);
  const screenShake = new ScreenShake();

  // --- Chest, Furnace, Trading & Tile Entities ---
  const tileEntities = new TileEntities(worldSlot);
  tileEntities.setWorld(world);
  const chestScreen = new ChestScreen(inventory);
  const furnaceScreen = new FurnaceScreen(inventory);
  const tradingScreen = new TradingScreen(inventory);

  // --- XP System & Enchanting ---
  const xpSystem = new XPSystem();
  const enchantScreen = new EnchantScreen(inventory, xpSystem);

  // --- Potion System ---
  const potionSystem = new PotionSystem();
  raidSystem.potionSystem = potionSystem;
  const potionEffectDisplay = new PotionEffectDisplay(potionSystem);
  const brewingScreen = new BrewingScreen(inventory);

  // --- Minimap, Achievements, Recipe Book ---
  const minimap = new Minimap(world);
  const achievements = new AchievementSystem();
  achievements.setSoundManager(soundManager);
  achievements.onUnlockCelebration = (def) => {
    achievementCelebration.celebrate(def.name, def.desc, def.icon);
    notifications.achievement(`${def.name} — ${def.desc}`, 5);
  };
  const recipeBook = new RecipeBook(craftingSystem);

  // --- Armor System ---
  const armorSystem = new ArmorSystem();
  healthBar.setArmorSystem(armorSystem);

  // --- Bed System ---
  const bedSystem = new BedSystem();

  // --- Grave Hide System ---
  const graveHideSystem = new GraveHideSystem();
  graveHideSystem.world = world;
  graveHideSystem.player = player;

  // --- Item Drop System (death drops) ---
  const itemDropSystem = new ItemDropSystem(scene);

  // --- Redstone System ---
  const redstoneSystem = new RedstoneSystem(world);
  const pistonSystem = new PistonSystem(world, tileEntities);
  const hopperSystem = new HopperSystem(world, tileEntities);

  // Wire piston power changes from redstone
  redstoneSystem.onPistonPower = (x, y, z, powered) => {
    pistonSystem.onPowerChange(x, y, z, powered);
    world.updateLoadedChunks(player.position);
  };

  // --- Breeding System ---
  const breedingSystem = new BreedingSystem(entityManager, scene);

  // --- Interactive Blocks ---
  const interactiveBlocks = new InteractiveBlocks(world, soundManager);

  // Wire up grave hide system references
  graveHideSystem.health = health;
  graveHideSystem.hud = hud;
  graveHideSystem.interactiveBlocks = interactiveBlocks;
  graveHideSystem.tileEntities = tileEntities;

  // --- Map System ---
  const mapItem = new MapItem(world);

  // --- Fishing System ---
  const fishingSystem = new FishingSystem(scene, camera, world, inventory, soundManager);

  // --- Boss System ---
  const bossSystem = new BossSystem(scene, world, soundManager);

  // Wither spawn cinematic video
  bossSystem.onWitherSpawn = () => {
    // Play wither.webm as fullscreen cinematic (3-5 seconds)
    fetch('/wither.webm', { method: 'HEAD' })
      .then(r => {
        if (!r.ok || !r.headers.get('content-type')?.startsWith('video/')) return;
        const overlay = document.createElement('div');
        overlay.style.cssText = 'position:fixed;inset:0;z-index:9500;background:#000;pointer-events:none;';
        const video = document.createElement('video');
        video.style.cssText = 'width:100%;height:100%;object-fit:cover;';
        video.playsInline = true;
        video.muted = true;
        video.src = '/wither.webm';
        overlay.appendChild(video);
        document.body.appendChild(overlay);
        video.play().catch(() => {});
        video.addEventListener('ended', () => {
          overlay.style.transition = 'opacity 0.5s';
          overlay.style.opacity = '0';
          setTimeout(() => overlay.remove(), 600);
        }, { once: true });
        // Safety timeout
        setTimeout(() => { if (overlay.parentNode) { overlay.style.opacity = '0'; setTimeout(() => overlay.remove(), 600); } }, 8000);
      })
      .catch(() => {});
  };

  // --- Tutorial ---
  const tutorial = new Tutorial();

  // --- Chat System ---
  const chatSystem = new ChatSystem();

  // --- Water Shader ---
  const waterShader = new WaterShader(scene);
  // Link chunk mesher's transparent material for animated water surface
  if (world.mesher && world.mesher.transparentMaterial) {
    waterShader.setTransparentMaterial(world.mesher.transparentMaterial);
  }

  // --- Cave Fog ---
  const caveFog = new CaveFog(scene);

  // --- Ambient Music ---
  const ambientMusic = new AmbientMusic(soundManager);

  // --- Game Mode Menu ---
  const gameModeMenu = new GameModeMenu();

  // --- Pause Menu ---
  const pauseMenu = new PauseMenu({
    onSave: () => doSave(),
    onResume: () => renderer.domElement.requestPointerLock(),
    onRenderDistance: (val) => {},
    onVolume: (val) => { soundManager.setVolume(val); ambientMusic.setVolume(val); },
    onSensitivity: (val) => fpsCamera.setSensitivity(val),
    onBrillo: (on) => { brilloActivo = on; },
    onResolucion: (val) => {
      renderer.setPixelRatio(val);
      renderer.setSize(window.innerWidth, window.innerHeight);
      if (composer) { composer.setPixelRatio?.(val); composer.setSize(window.innerWidth, window.innerHeight); }
    },
    onMultiplayer: () => {
      pauseMenu.close();
      multiplayerMenu.show();
    },
    onSkin: () => {
      pauseMenu.close();
      skinSelector.show();
    },
    onCompartir: async () => {
      /* Se comparte lo que hay AHORA, no lo último guardado: si Fede acaba de
         poner una torre y comparte, la torre tiene que ir en el enlace. */
      const codigo = await codificar({
        nombre: saveManager.worldName,
        jugador: { x: player.position.x, y: player.position.y, z: player.position.z,
                   yaw: fpsCamera.yaw, pitch: fpsCamera.pitch },
        bloques: saveManager.modifications,
      });
      return { url: urlDeCodigo(codigo), largo: codigo.length, limite: LIMITE_COMODO,
               bloques: saveManager.modifications.size };
    },
    onGameMode: () => {
      pauseMenu.close();
      gameModeMenu.show().then((result) => {
        const mode = result.mode || result;
        const difficulty = result.difficulty || 'normal';
        terminal.gameMode = mode;
        currentDifficulty = difficulty;
        eventBus.emit(GameEvents.GAME_MODE_CHANGED, { mode, difficulty });
        if (mode === GameMode.CREATIVE) {
          hud.showMessage('Modo Creativo activado', TELEPORT_MSG_DURATION);
        } else {
          hud.showMessage(`Modo Supervivencia (${difficulty}) activado`, TELEPORT_MSG_DURATION);
        }
        renderer.domElement.requestPointerLock();
      });
    },
  });

  // --- P2P Multiplayer ---
  const p2pManager = new P2PManager();
  const multiplayerMenu = new MultiplayerMenu(p2pManager);

  // --- Phone & Computer screens (apps: Teams, Waze, Mail, Safari) ---
  const phoneScreen = new PhoneScreen({ world, player });
  const computerScreen = new ComputerScreen({ world, player });

  // Remote player mesh (Steve-like colored box figure)
  let remotePlayerMesh = null;
  const _remoteTargetPos = new THREE.Vector3();
  let _remoteTargetYaw = 0;
  let _remoteVisible = false;

  /* El jugador remoto usa el MISMO modelo que el local: antes era otra figura
     con otros colores a mano, así que en multijugador los dos se veían
     distintos aunque llevaran la misma skin. */
  function createRemotePlayerMesh() {
    const group = crearModelo(skinSelector.getCurrentSkin());
    // Etiqueta con el nombre, flotando sobre la cabeza
    const canvas = document.createElement('canvas');
    canvas.width = 128; canvas.height = 32;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'rgba(0,0,0,0.5)';
    ctx.fillRect(0, 0, 128, 32);
    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 18px monospace';
    ctx.textAlign = 'center';
    ctx.fillText('Jugador 2', 64, 22);
    const tex = new THREE.CanvasTexture(canvas);
    const tag = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, depthTest: false }));
    tag.scale.set(1.2, 0.3, 1);
    tag.position.y = 2.2;
    group.add(tag);
    group.visible = false;
    return group;
  }

  /* Se construye más abajo, junto al selector: necesita saber qué skin hay
     elegida y el selector todavía no existe en este punto. */

  // P2P message handler
  p2pManager.onMessage = (msg) => {
    if (msg.type === 'pos' && msg.data) {
      _remoteTargetPos.set(msg.data.x, msg.data.y, msg.data.z);
      _remoteTargetYaw = msg.data.yaw || 0;
      if (!_remoteVisible) {
        _remoteVisible = true;
        remotePlayerMesh.visible = true;
        remotePlayerMesh.position.copy(_remoteTargetPos);
      }
    } else if (msg.type === 'block' && msg.data) {
      const d = msg.data;
      world.setBlock(d.x, d.y, d.z, d.b);
      world.updateLoadedChunks(player.position);
    } else if (msg.type === 'chat' && msg.data) {
      chatSystem.addSystemMessage('[P2] ' + msg.data.text);
    }
  };

  p2pManager.onConnect = () => {
    hud.showMessage('Jugador 2 conectado!', 5);
    multiplayerMenu._showConnectedState(true);
  };

  p2pManager.onDisconnect = () => {
    hud.showMessage('Jugador 2 desconectado', 5);
    _remoteVisible = false;
    if (remotePlayerMesh) remotePlayerMesh.visible = false;
    multiplayerMenu._showConnectedState(false);
  };

  // --- Screenshot ---
  const screenshot = new Screenshot(renderer);

  // --- Screen Effects ---
  const screenEffects = new ScreenEffects();

  // --- Post-Processing & Clouds ---
  const postProcessing = new PostProcessing();
  const cloudSystem = new CloudSystem(scene);

  // --- Touch Controls (mobile) ---
  const touchControls = new TouchControls(inputManager);

  // --- Cloud Save ---
  const cloudSave = new CloudSave();

  // --- Stats Screen ---
  const statsScreen = new StatsScreen();

  /* El herrero. Se abre desde la mesa de encantamientos, que es donde Fede ya
     va a mejorar cosas: no hacía falta un bloque nuevo que aprender. */
  const herreroScreen = new HerreroScreen(inventory, (m) => hud.showMessage(m));

  // --- Skin Selector ---
  const skinSelector = new SkinSelector();

  /* El cuerpo se construye con la skin guardada y se vuelve a vestir cada vez
     que Fede elige otra. Vestir NO rehace la geometría: solo repinta la
     textura, así que cambiar de skin no tiene coste apreciable. */
  tpGroup = crearModelo(skinSelector.getCurrentSkin());
  tpGroup.visible = false;
  scene.add(tpGroup);
  fpsCamera.setPlayerMesh(tpGroup);

  remotePlayerMesh = createRemotePlayerMesh();
  scene.add(remotePlayerMesh);

  /* Asa para las pruebas y para depurar a mano.
     Sin esto no hay forma de comprobar desde fuera que cambiar de skin cambia
     algo, que es justo el fallo que tenía la versión anterior: el selector
     existía, se abría, y no tocaba nada. */
  window.__fw = {
    cuerpo: () => tpGroup,
    lienzoSkin: () => tpGroup?.userData?.textura?.image || null,
    selector: () => skinSelector,
    camara: () => camera,
    escena: () => scene,
    render: () => renderer,
    mallador: () => world.mesher,
    mundo: () => world,
    botin: () => itemDropManager,
    mazmorra: () => mazmorra,
    hostiles: () => hostileMobs,
    jugador: () => player,
    inventario: () => inventory,
  };
  window.__tpSkinCanvas = window.__fw.lienzoSkin;   // compatibilidad
  /* En getter, no asignada: __fw se define aquí arriba y `mazmorra` se declara
     seiscientas líneas más abajo. Asignarla directamente entra en la zona
     muerta temporal y mata el arranque entero. Dentro de una función solo se
     evalúa cuando alguien la llama, que es después. */
  Object.defineProperty(window, '__mazmorra', { get: () => mazmorra, configurable: true });

  skinSelector.onSkinChanged = (skin) => {
    vestirModelo(tpGroup, skin);
    if (remotePlayerMesh) vestirModelo(remotePlayerMesh, skin);
    hud.showMessage(`Skin: ${skin.nombre}`);
  };

  // --- Damage Numbers ---
  const damageNumbers = new DamageNumbers(scene, camera, renderer);

  // --- Magic System ---
  const magicSystem = new MagicSystem();
  magicSystem.player = player;
  magicSystem.camera = camera;
  magicSystem.world = world;
  magicSystem.health = health;
  magicSystem.particles = particles;
  magicSystem.hud = hud;
  magicSystem.scene = scene;
  magicSystem.soundManager = soundManager;
  magicSystem.xpSystem = xpSystem;

  // --- Combo System ---
  const comboSystem = new ComboSystem();

  // --- Damage Direction Indicator ---
  const damageDirection = new DamageDirection();

  // --- Accessibility & HUD Config ---
  const accessibilityMenu = new AccessibilityMenu();
  const hudConfig = new HUDConfig();

  // --- Season System ---
  const seasonSystem = new SeasonSystem();
  seasonSystem.onSeasonChange = (season) => {
    notifications.achievement(`${season.icon} Nueva estación: ${season.name}`, 6);
  };

  // --- Challenge Mode ---
  const challengeMode = new ChallengeMode();
  challengeMode.onChallengeComplete = (c) => {
    notifications.success(`✓ Desafío: ${c.title}`, 5);
    achievementCelebration.celebrate(c.title, c.desc, '🏆');
    inventory.addItem(-16, 5); // 5 emeralds per challenge
    xpSystem.addXP(50);
  };
  challengeMode.onChallengeUnlocked = (c) => {
    notifications.warning(`Nuevo desafío día ${c.day}: ${c.title}`, 5);
  };
  challengeMode.onAllComplete = (stats) => {
    achievementCelebration.celebrate(
      '¡DESAFÍO COMPLETADO!',
      `${stats.timeHours.toFixed(1)} horas · ${stats.deaths} muertes`,
      '👑'
    );
  };

  // --- Speedrun Timer ---
  const speedrunTimer = new SpeedrunTimer();
  speedrunTimer.onMilestone = (id, ms) => {
    notifications.success(`⏱ ${id} — ${speedrunTimer._formatTime(ms)}`, 3);
  };
  speedrunTimer.onNewBest = (id, ms) => {
    notifications.achievement(`🏆 Nuevo récord: ${speedrunTimer._getLabel(id)}`, 5);
  };

  // --- Dynamic Events ---
  const dynamicEvents = new DynamicEvents();
  dynamicEvents.scene = scene;
  dynamicEvents.world = world;
  dynamicEvents.player = player;
  dynamicEvents.entityManager = entityManager;
  dynamicEvents.hud = hud;
  dynamicEvents.particles = particles;
  dynamicEvents.soundManager = soundManager;
  dynamicEvents.health = health;
  dynamicEvents._onSpawnInvasionMobs = (type, count, center) => {
    // Reuse raid spawn mechanism
    if (raidSystem.onSpawnMobs) raidSystem.onSpawnMobs(type, count, center);
  };
  dynamicEvents._onSpawnBossMob = (center) => {
    // Spawn a stronger variant of a hostile mob
    if (raidSystem.onSpawnMobs) {
      const mobs = ['ZOMBIE', 'SKELETON', 'CREEPER', 'WITCH', 'PILLAGER'];
      const type = mobs[Math.floor(Math.random() * mobs.length)];
      raidSystem.onSpawnMobs(type, 1, center);
      // Buff the last spawned one
      const lastMob = hostileMobs[hostileMobs.length - 1];
      if (lastMob) {
        lastMob.health *= 5;
        lastMob.maxHealth *= 5;
        lastMob.isLegendary = true;
        if (lastMob.mesh) lastMob.mesh.scale.multiplyScalar(1.5);
      }
    }
  };

  // --- Skill Tree ---
  const skillTree = new SkillTree();
  skillTree.onPointsGained = (n) => {
    hud.showMessage(`+${n} punto${n > 1 ? 's' : ''} de habilidad — presiona K`, 4);
  };
  skillTree.onSkillUnlocked = (s) => {
    hud.showMessage(`🎓 ${s.name} desbloqueada`, 3);
    soundManager.playLevelUp?.();
  };

  // --- Quest System ---
  const questSystem = new QuestSystem();
  questSystem.onQuestComplete = (quest) => {
    // Give rewards
    inventory.addItem(-16, quest.reward); // EMERALD_GEM
    xpSystem.addXP(quest.xp);
    hud.showMessage(`¡Misión completada! +${quest.reward} esmeraldas, +${quest.xp} XP`, 4);
    soundManager.playLevelUp?.();
    damageNumbers.spawn(player.position, quest.reward, 'heal');
  };

  // --- Horse mounting state ---
  let mountedHorse = null;

  // --- Bow System ---
  const bowSystem = new BowSystem(scene, camera, inventory);

  // --- Wire up BlockInteraction callbacks ---
  blockInteraction.onOpenChest = (x, y, z) => {
    const chestData = tileEntities.getDoubleChest(x, y, z);
    document.exitPointerLock();
    chestScreen.open(chestData, () => {
      tileEntities.save(chestData);
    });
  };

  blockInteraction.onOpenCrafting3x3 = () => {
    document.exitPointerLock();
    craftingScreen.toggle(true); // true = 3x3 mode
  };

  blockInteraction.onUseBed = (x, y, z) => {
    // Use BedSystem for proper sleep mechanics
    const error = bedSystem.tryToSleep(x, y, z, dayNight.isNight(), hostileMobs);
    if (error) {
      bedSystem.showMessage(error);
    }
  };

  blockInteraction.onOpenFurnace = (x, y, z) => {
    const furnaceData = tileEntities.getFurnace(x, y, z);
    document.exitPointerLock();
    furnaceScreen.open(furnaceData, (updatedData) => {
      tileEntities.setFurnace(x, y, z, updatedData);
      tileEntities.save();
    });
  };

  blockInteraction.onOpenEnchantTable = () => {
    document.exitPointerLock();
    enchantScreen.open();
    playBriefVideo('/enchant.webm', 2);
  };

  /* Agacharse (Shift) sobre la mesa abre el herrero en vez del encantador.
     Un solo bloque, dos servicios — como el hub de Dungeons, pero sin pedirle
     a Fede que recuerde otro bloque más. */
  blockInteraction.onOpenBlacksmith = () => {
    document.exitPointerLock();
    herreroScreen.open();
  };

  blockInteraction.onOpenBrewingStand = () => {
    document.exitPointerLock();
    brewingScreen.open();
  };

  blockInteraction.onActivatePortal = (x, y, z) => {
    if (activatePortal(world, x, y, z)) {
      teleportMsg = 'Portal del Nether activado!';
      teleportMsgTime = TELEPORT_MSG_DURATION;
      soundManager.playExplosion();
    }
  };

  // --- Redstone callbacks ---
  blockInteraction.onToggleLever = (x, y, z) => {
    redstoneSystem.toggleLever(x, y, z);
  };
  blockInteraction.onPressButton = (x, y, z) => {
    redstoneSystem.pressButton(x, y, z);
  };

  // --- Interactive blocks callbacks ---
  blockInteraction.onToggleTrapdoor = (x, y, z) => {
    interactiveBlocks.toggleTrapdoor(x, y, z);
  };
  blockInteraction.onEditSign = (x, y, z) => {
    const currentText = interactiveBlocks.getSignText(x, y, z);
    const newText = prompt('Escribe en el cartel:', currentText);
    if (newText !== null) {
      interactiveBlocks.setSignText(x, y, z, newText);
    }
  };
  blockInteraction.onToggleJukebox = (x, y, z) => {
    interactiveBlocks.toggleJukebox(x, y, z);
  };
  blockInteraction.onChangeNote = (x, y, z) => {
    interactiveBlocks.changeNotePitch(x, y, z);
  };
  blockInteraction.onToggleCoffin = (x, y, z) => {
    const block = world.getBlock(x, y, z);
    if (block === BlockId.COFFIN_OPEN) {
      // Already open — enter hiding mode
      graveHideSystem.enterGrave(x, y, z);
    } else {
      // Closed — open it
      interactiveBlocks.toggleCoffin(x, y, z);
      world.updateLoadedChunks(player.position);
      hud.showMessage('Ataúd abierto — Click izquierdo de nuevo para esconderte');
      soundManager.playDoorToggle?.();
    }
  };

  // --- End Portal Eye placement ---
  blockInteraction.onPlaceEnderEye = (x, y, z) => {
    const selected = inventory.getSelectedSlot();
    if (!selected || selected.itemId !== -87) {  // ENDER_EYE = -87
      hud.showMessage('Necesitas un Ojo de Ender');
      return;
    }
    // Check if already has eye
    const key = `${x},${y},${z}`;
    const te = tileEntities.data.get(key);
    if (te && te.hasEye) {
      hud.showMessage('Este marco ya tiene un ojo');
      return;
    }
    // Place eye, consume item
    tileEntities.data.set(key, { type: 'end_frame', hasEye: true });
    tileEntities.save();
    inventory.removeFromSelected(1);
    soundManager.playPlace();
    hud.showMessage('Ojo de Ender colocado');
    // Check if portal is now complete (scan for center)
    // Try each possible center offset relative to this frame position
    for (let dx = -2; dx <= 2; dx++) {
      for (let dz = -2; dz <= 2; dz++) {
        if (isEndPortalComplete(world, x + dx, y, z + dz, tileEntities)) {
          activateEndPortal(world, x + dx, y, z + dz);
          world.updateLoadedChunks(player.position);
          hud.showMessage('¡Portal del End activado!');
          soundManager.playExplosion();
          screenShake.addExplosion(0.5);
          return;
        }
      }
    }
  };

  // --- Redstone TNT ignition ---
  redstoneSystem.onTNTIgnite = (x, y, z) => {
    const destroyed = interactiveBlocks.explodeTNT(x, y, z);
    soundManager.playExplosion();
    screenShake.addExplosion(1.0);
    world.updateLoadedChunks(player.position);
  };

  // --- Block Broken: EventBus subscribers ---
  eventBus.on(GameEvents.BLOCK_BROKEN, ({ x, y, z, blockId }) => {
    ModAPI.fireEvent('onBlockBreak', x, y, z, blockId);
  });
  eventBus.on(GameEvents.BLOCK_BROKEN, () => {
    if (tutorial.isActive()) tutorial.onBlockBroken();
  });
  eventBus.on(GameEvents.BLOCK_BROKEN, ({ x, y, z }) => {
    tileEntities.onBlockRemoved(x, y, z);
    interactiveBlocks.onBlockRemoved(x, y, z);
    redstoneSystem.onBlockChanged(x, y, z, BlockId.AIR);
    bedSystem.onBedDestroyed(x, y, z);
  });
  eventBus.on(GameEvents.BLOCK_BROKEN, () => {
    health.onMine();
  });
  eventBus.on(GameEvents.BLOCK_BROKEN, ({ x, y, z, blockId }) => {
    particles.spawnBlockBreak(new THREE.Vector3(x + 0.5, y + 0.5, z + 0.5), 0x888888);
    achievements.onBlockMined(blockId);
    questSystem.onBlockMined(blockId);
    statsScreen.onBlockMined();
    // Speedrun milestones
    if (blockId === BlockId.WOOD || blockId === BlockId.LOG_BIRCH || blockId === BlockId.JUNGLE_LOG || blockId === BlockId.ACACIA_LOG) speedrunTimer.mark('wood');
    if (blockId === BlockId.IRON_ORE) speedrunTimer.mark('iron');
    if (blockId === BlockId.DIAMOND_ORE) speedrunTimer.mark('diamond');
    // Challenge: first wood, first diamond
    if (blockId === BlockId.WOOD && challengeMode.active) challengeMode.markComplete('c1');
    if (blockId === BlockId.DIAMOND_ORE && challengeMode.active) challengeMode.markComplete('c10');
  });
  eventBus.on(GameEvents.BLOCK_BROKEN, ({ x, y, z, blockId }) => {
    const oreBlocks = [BlockId.COAL_ORE, BlockId.IRON_ORE, BlockId.GOLD_ORE, BlockId.DIAMOND_ORE, BlockId.LAPIS_ORE];
    let mineXP = 1;
    if (oreBlocks.includes(blockId)) {
      mineXP = blockId === BlockId.DIAMOND_ORE ? 7 : blockId === BlockId.LAPIS_ORE ? 4 : 3;
    }
    const blockPos = new THREE.Vector3(x + 0.5, y + 0.5, z + 0.5);
    particles.spawnXPOrb(blockPos, player.position, Math.min(mineXP, 5));
    soundManager.playXPPickup();
    const levelsGained = xpSystem.addXP(mineXP);
    if (levelsGained > 0) { soundManager.playLevelUp(); particles.spawnLevelUpBurst(player.position); playLevelUpVideo(); }
  });
  eventBus.on(GameEvents.BLOCK_BROKEN, ({ x, y, z, blockId }) => {
    waterPhysics.onBlockChanged(x, y, z, blockId, 0);
  });

  // BlockInteraction emits to EventBus
  blockInteraction.onBlockBroken = (x, y, z, blockId, hadDrop) => {
    eventBus.emit(GameEvents.BLOCK_BROKEN, { x, y, z, blockId, hadDrop });
    // P2P: sync block break (set to AIR)
    if (p2pManager.isConnected) {
      p2pManager.sendBlockChange(x, y, z, 0);
    }
  };

  // --- Mob Kill: EventBus subscribers ---
  eventBus.on(GameEvents.MOB_KILLED, ({ type, position }) => {
    particles.spawnDeathSmoke(position);
    ModAPI.fireEvent('onMobKill', type, position.x, position.y, position.z);
    const dropPos = position.clone();
    dropPos.y += 0.5;
    const drops = rollMobDrops(type);
    for (const d of drops) {
      if (d.isBlock) {
        itemDropManager.dropItem(d.itemId, d.count, dropPos, true);
      } else {
        /* La rareza se tira aquí, al caer: es lo que hace que dos espadas de
           hierro no sean la misma espada de hierro y merezca la pena mirar
           qué soltó el mob. La suerte del jugador empuja hacia arriba. */
        const conRareza = admiteRareza(d.itemId, false)
          ? tirarObjeto({ suerte: suerteDelJugador() })
          : null;
        itemDropManager.dropNonBlockItem(d.itemId, d.count, dropPos, true, conRareza);
        if (conRareza && conRareza.rareza !== 'comun' && conRareza.rareza !== 'buena') {
          hud.showMessage(`¡${describir({ ...conRareza })}!`);
          soundManager.playLevelUp?.();
        }
      }
    }
    const baseMobXP = MOB_XP[type] || 3;
    const mobXP = Math.ceil(baseMobXP * dynamicEvents.getXPMultiplier());
    particles.spawnXPOrb(dropPos, player.position, mobXP);
    if (xpSystem.addXP(mobXP) > 0) { soundManager.playLevelUp(); particles.spawnLevelUpBurst(player.position); playLevelUpVideo(); }
    achievements.onKillMob(type);
    questSystem.onMobKilled(type);
    statsScreen.onMobKilled();
    if (type === 'blaze') speedrunTimer.mark('blaze');
    if (type === 'enderman') speedrunTimer.mark('enderpearl');
    if (type === 'wither' && challengeMode.active) challengeMode.markComplete('c30');
  });

  // --- Game Mode Change: EventBus subscribers ---
  eventBus.on(GameEvents.GAME_MODE_CHANGED, ({ mode, difficulty }) => {
    health.creativeMode = (mode === GameMode.CREATIVE || mode === GameMode.SPECTATOR);
    player.noCollision = (mode === GameMode.SPECTATOR);
    if (difficulty) {
      currentDifficulty = difficulty;
      health.difficultyConfig = DIFFICULTY_CONFIG[difficulty] || null;
    }
    if (mode === GameMode.CREATIVE) {
      player.flying = true;
      health.health = health.maxHealth;
      health.hunger = health.maxHunger;
    } else {
      inventory.clearAll();
    }
    // Peaceful: despawn all hostile mobs
    const dc = DIFFICULTY_CONFIG[currentDifficulty];
    if (dc && !dc.hostileMobs) {
      for (const hm of hostileMobs) { disposeHostileMob(hm); }
      hostileMobs.length = 0;
    }
  });

  blockInteraction.onEatFood = (itemId, foodValue) => {
    health.feed(foodValue.hunger, foodValue.saturation || Math.floor(foodValue.hunger * 0.6));
    soundManager.playEat();
    achievements.onEatFood();
    // Handle potion effects
    if (foodValue.potionEffect) {
      const effectId = foodValue.potionEffect;
      const def = POTION_EFFECTS[effectId];
      if (def) {
        if (def.duration <= 0 && def.healAmount) {
          // Instant healing
          health.heal(def.healAmount);
        } else {
          potionSystem.applyEffect(effectId);
        }
      }
    }
  };

  // --- Water Physics ---
  const waterPhysics = new WaterPhysics(world);

  // --- Farming System ---
  const farmingSystem = new FarmingSystem(world);
  world.farmingSystem = farmingSystem;

  blockInteraction.onBlockPlaced = (blockId, x, y, z) => {
    // P2P: sync block placement
    if (p2pManager.isConnected) {
      p2pManager.sendBlockChange(x, y, z, blockId);
    }
    ModAPI.fireEvent('onBlockPlace', x, y, z, blockId);
    if (tutorial.isActive()) tutorial.onBlockPlaced();
    achievements.onBlockPlaced(blockId);
    // Notify water physics about the placed block
    waterPhysics.onBlockChanged(x, y, z, 0, blockId);
    // Notify redstone system about new block
    redstoneSystem.onBlockChanged(x, y, z, blockId);
    // Auto-place grave structure when placing coffin
    if (blockId === BlockId.COFFIN) {
      // Place GRAVE_BASE below and CROSS beside
      const below = world.getBlock(x, y - 1, z);
      if (below !== BlockId.GRAVE_BASE) {
        world.setBlock(x, y - 1, z, BlockId.GRAVE_BASE);
      }
      // Try placing cross on +X side, or -X if blocked
      const crossX1 = world.getBlock(x + 1, y, z);
      const crossX2 = world.getBlock(x - 1, y, z);
      if (crossX1 === BlockId.AIR) {
        world.setBlock(x + 1, y, z, BlockId.CROSS);
      } else if (crossX2 === BlockId.AIR) {
        world.setBlock(x - 1, y, z, BlockId.CROSS);
      }
      // Save as grave in tile entities
      tileEntities.saveGrave(x, y, z);
      hud.showMessage(`Tumba creada en (${x}, ${y}, ${z})`);
    }
    // Check if soul sand placement summons the Wither
    if (blockId === BlockId.SOUL_SAND) {
      if (bossSystem.checkWitherSummon(x, y, z)) {
        world.updateLoadedChunks(player.position);
        teleportMsg = 'El Wither ha sido invocado!';
        teleportMsgTime = TELEPORT_MSG_DURATION;
      }
    }
    // Register hopper when placed
    if (blockId === BlockId.HOPPER) {
      hopperSystem.registerHopper(x, y, z);
    }
    // Store piston facing when placed (facing up by default — simplified)
    if (blockId === BlockId.PISTON || blockId === BlockId.STICKY_PISTON) {
      pistonSystem.setPistonFacing(x, y, z, 0); // default facing up
    }
    // Check golem patterns when placing PUMPKIN
    if (blockId === BlockId.PUMPKIN) {
      // Iron Golem: T-shape of 4 IRON_BLOCK + PUMPKIN on top center
      // Check below is IRON_BLOCK and 2 more to the sides at same level
      const below = world.getBlock(x, y - 1, z);
      if (below === BlockId.IRON_BLOCK) {
        // Check T-shape: 3 horizontal + 1 vertical
        const b2 = world.getBlock(x, y - 2, z);
        // Try X-axis T
        if (world.getBlock(x - 1, y - 1, z) === BlockId.IRON_BLOCK &&
            world.getBlock(x + 1, y - 1, z) === BlockId.IRON_BLOCK && b2 === BlockId.AIR) {
          // Remove pattern blocks
          world.setBlock(x, y, z, BlockId.AIR);
          world.setBlock(x, y - 1, z, BlockId.AIR);
          world.setBlock(x - 1, y - 1, z, BlockId.AIR);
          world.setBlock(x + 1, y - 1, z, BlockId.AIR);
          entityManager.spawnEntity('IRON_GOLEM', x, y - 1, z);
          world.updateLoadedChunks(player.position);
          hud.showMessage('¡Iron Golem invocado!');
          playBriefVideo('/golem.webm', 3);
        }
        // Try Z-axis T
        else if (world.getBlock(x, y - 1, z - 1) === BlockId.IRON_BLOCK &&
                 world.getBlock(x, y - 1, z + 1) === BlockId.IRON_BLOCK && b2 === BlockId.AIR) {
          world.setBlock(x, y, z, BlockId.AIR);
          world.setBlock(x, y - 1, z, BlockId.AIR);
          world.setBlock(x, y - 1, z - 1, BlockId.AIR);
          world.setBlock(x, y - 1, z + 1, BlockId.AIR);
          entityManager.spawnEntity('IRON_GOLEM', x, y - 1, z);
          world.updateLoadedChunks(player.position);
          hud.showMessage('¡Iron Golem invocado!');
          playBriefVideo('/golem.webm', 3);
        }
      }
      // Snow Golem: 2 SNOW vertical + PUMPKIN on top
      const snowBelow1 = world.getBlock(x, y - 1, z);
      const snowBelow2 = world.getBlock(x, y - 2, z);
      if (snowBelow1 === BlockId.SNOW && snowBelow2 === BlockId.SNOW) {
        world.setBlock(x, y, z, BlockId.AIR);
        world.setBlock(x, y - 1, z, BlockId.AIR);
        world.setBlock(x, y - 2, z, BlockId.AIR);
        entityManager.spawnEntity('SNOW_GOLEM', x, y - 1, z);
        world.updateLoadedChunks(player.position);
        hud.showMessage('¡Snow Golem invocado!');
      }
    }
  };

  blockInteraction.onCropPlanted = (x, y, z) => {
    farmingSystem.plantCrop(x, y, z);
  };

  blockInteraction.onBoneMeal = (x, y, z) => {
    return farmingSystem.applyBoneMeal(x, y, z);
  };

  // --- Hostile Mobs ---
  const hostileMobs = [];

  /* ── LA MAZMORRA ────────────────────────────────────────────────────────
     No inventa contenido: orquesta lo que ya había suelto —mobs hostiles,
     BossSystem, el botín de Rareza— y le pone una forma con principio y final.
     Los mobs que invoca van marcados con `deMazmorra` para poder contarlos
     sin confundirlos con la fauna normal de la noche. */
  const mazmorra = new ModoMazmorra({
    onAviso: (t) => hud.showMessage(t),
    onGenerarMobs: (tipos, cuantos) => {
      for (let i = 0; i < cuantos; i++) {
        const antes = hostileMobs.length;
        spawnHostileMob(player.position, tipos[i % tipos.length].toUpperCase());
        for (let k = antes; k < hostileMobs.length; k++) hostileMobs[k].deMazmorra = true;
      }
    },
    onInvocarJefe: () => {
      const p = player.position;
      /* _spawnWither es privado en BossSystem pero es el único camino: la vía
         pública, checkWitherSummon, exige que el jugador construya la T de
         arena de almas, y en una mazmorra el jefe lo pone la mazmorra. */
      bossSystem._spawnWither(p.x + 14, p.y + 4, p.z);
    },
    onBotin: (cuantos, suerte) => {
      /* El botín cae a los pies, no en el sitio del mob: al terminar una
         oleada Fede está donde está, y hacerle rastrear el mapa rompe el
         ritmo justo cuando debería estar disfrutando el premio. */
      for (let i = 0; i < cuantos; i++) {
        const pos = player.position.clone();
        pos.x += (Math.random() - 0.5) * 3;
        pos.z += (Math.random() - 0.5) * 3;
        pos.y += 1;
        itemDropManager.dropNonBlockItem(
          ItemId.DIAMOND_GEM, 1, pos, true,
          tirarObjeto({ suerte: suerte + suerteDelJugador() }));
      }
    },
    onFin: (gano, r) => {
      hud.showMessage(gano
        ? `Mazmorra completada · ${r.bajas} enemigos · ${r.segundos}s`
        : `Mazmorra fallida en la oleada ${r.oleadasSuperadas + 1} de ${r.totalOleadas}`);
      for (const m of hostileMobs) m.deMazmorra = false;
    },
    vivos: () => hostileMobs.filter((m) => m.deMazmorra && m.health > 0).length
                 + (bossSystem.wither ? 1 : 0),
  });

  magicSystem.hostileMobs = hostileMobs;
  dynamicEvents.hostileMobs = hostileMobs;
  const arrows = [];
  const fireballs = [];
  let hostileSpawnTimer = 0;

  /** Properly remove and dispose a hostile mob's mesh and all its GPU resources */
  function disposeHostileMob(hm) {
    if (!hm.mesh) return;
    scene.remove(hm.mesh);
    hm.mesh.traverse(child => {
      if (child.geometry) child.geometry.dispose();
      if (child.material) {
        if (child.material.map) child.material.map.dispose();
        child.material.dispose();
      }
    });
    hm.mesh = null;
  }

  // --- Set villa positions for entity spawning ---
  const villaMiseriaPos = terrainGenerator.getVillaMiseriaPosition();
  const villaProsperidadPos = terrainGenerator.getVillaProsperidadPosition();
  const ciudadPos = terrainGenerator.getCiudadPosition();
  entityManager.setVillaPositions(villaMiseriaPos, villaProsperidadPos);
  if (entityManager.setCiudadPosition) entityManager.setCiudadPosition(ciudadPos);
  // Expose to world for the phone Waze app
  world._villaMiseriaPos = villaMiseriaPos;
  world._villaProsperidadPos = villaProsperidadPos;
  world._ciudadPos = ciudadPos;

  // Add minimap markers for villages
  minimap.addMarker(villaMiseriaPos.x, villaMiseriaPos.z, '#FF4444', 'VM');
  minimap.addMarker(villaProsperidadPos.x, villaProsperidadPos.z, '#44FF44', 'VP');
  minimap.addMarker(ciudadPos.x, ciudadPos.z, '#33CCFF', 'C');
  minimap.addMarker(0, 0, '#FFFF00', 'Spawn');

  /* ── Mundo compartido por enlace ────────────────────────────────────────
     Si la URL trae ?m=..., lo que se carga es el mundo de otra persona.

     NO se toca ninguna ranura: el mundo compartido vive solo en memoria. Si
     escribiera en un slot, abrir el enlace de un amigo le borraría a Fede lo
     que tiene construido, y eso es imperdonable. Por eso también se apaga el
     guardado automático mientras se está de visita. */
  const codigoCompartido = codigoDeUrl();
  let deVisita = false;
  if (codigoCompartido) {
    try {
      const m = await decodificar(codigoCompartido);
      for (const [k, id] of m.bloques) saveManager.modifications.set(k, id);
      if (m.jugador) {
        player.position.set(m.jugador.x, m.jugador.y, m.jugador.z);
        player.prevPosition.copy(player.position);
        fpsCamera.yaw = m.jugador.yaw || 0;
        fpsCamera.pitch = m.jugador.pitch || 0;
      }
      saveManager.update = () => {};          // nada de autoguardado de visita
      saveManager.save = () => {};
      deVisita = true;
      hud.showMessage(m.nombre ? `Estás en: ${m.nombre}` : 'Mundo compartido');
    } catch (e) {
      console.warn('[FedeWorld] código de mundo inválido:', e.message);
      hud.showMessage('Ese enlace no se entiende. Empiezas en tu mundo.');
    }
  }

  // --- Load saved data (skip if new world) ---
  let savedData = null;
  if (deVisita) {
    savedData = null;                          // el mundo lo manda el enlace
  } else if (gameOptions.newWorld) {
    saveManager.clearSave();
    localStorage.removeItem('voxelgame_tileentities');
    localStorage.removeItem('voxelgame_achievements');
  } else {
    savedData = saveManager.loadModifications();
  }
  if (savedData && savedData.player) {
    player.position.set(savedData.player.x, savedData.player.y, savedData.player.z);
    player.prevPosition.copy(player.position);
    if (savedData.player.flying) player.flying = true;
    // Restore health & hunger
    if (savedData.player.health !== undefined) health.health = savedData.player.health;
    if (savedData.player.hunger !== undefined) health.hunger = savedData.player.hunger;
    // Restore XP
    if (savedData.player.xp) xpSystem.deserialize(savedData.player.xp);
    // Restore potion effects
    if (savedData.player.potions) {
      for (const p of savedData.player.potions) {
        if (p.timeLeft > 0) potionSystem.applyEffect(p.id, p.timeLeft, p.level);
      }
    }
    // Restore game mode (deferred - terminal not yet created)
    var _savedGameMode = savedData.player.gameMode || null;
    // Restore armor
    if (savedData.player.armor) armorSystem.deserialize(savedData.player.armor);
    // Restore bed spawn
    if (savedData.player.bed) bedSystem.deserialize(savedData.player.bed);
    // Restore saturation
    if (savedData.player.saturation !== undefined) health.saturation = savedData.player.saturation;
    // Restore redstone
    if (savedData.player.redstone) redstoneSystem.deserialize(savedData.player.redstone);
    // Restore interactive blocks
    if (savedData.player.interactiveBlocks) interactiveBlocks.deserialize(savedData.player.interactiveBlocks);
    // Restore day/night, weather, farming
    if (savedData.player.dayNight) dayNight.deserialize(savedData.player.dayNight);
    if (savedData.player.weather) weather.deserialize(savedData.player.weather);
    if (savedData.player.farming) farmingSystem.deserialize(savedData.player.farming);
    // Restore dimension
    if (savedData.player.dimension === 'nether') {
      dimensionManager.currentDimension = 'nether';
      world.setGenerator(netherGenerator);
      scene.fog.color.set(NETHER_SKY_COLOR);
      scene.background.set(NETHER_SKY_COLOR);
      scene.fog.near = NETHER_FOG_NEAR;
      scene.fog.far = NETHER_FOG_FAR;
      dayNight.setVisible(false);
      cloudSystem.setVisible(false);
    }
  }
  if (savedData && savedData.inventory) {
    inventory.deserialize(savedData.inventory);
  }

  // --- Loading Screen + Spawn Player ---
  const loadingScreen = new LoadingScreen();

  if (!savedData || !savedData.player) {
    speedrunTimer.start();
    const spawnHeight = terrainGenerator.getHeight(0, 0) + 2;
    player.position.set(0, spawnHeight, 0);
    player.prevPosition.copy(player.position);
  }

  // Load chunks with loading screen (synchronous but with visual feedback via setTimeout)
  world.updateLoadedChunks(player.position);
  let chunksLoaded = 0;
  const totalChunks = 200;
  function loadChunkBatch() {
    const batchSize = 10;
    for (let i = 0; i < batchSize && chunksLoaded < totalChunks; i++) {
      world.updateLoadedChunks(player.position);
      chunksLoaded++;
    }
    const progress = chunksLoaded / totalChunks;
    loadingScreen.setProgress(progress, `Generando mundo... ${Math.floor(progress * 100)}%`);
    if (chunksLoaded < totalChunks) {
      setTimeout(loadChunkBatch, 0);
    } else {
      loadingScreen.hide();

      // Helper to apply game mode
      const applyGameMode = (mode) => {
        terminal.gameMode = mode;
        eventBus.emit(GameEvents.GAME_MODE_CHANGED, { mode });
      };

      if (savedData && savedData.player) {
        // Continue saved game
        startGameLoop();
        renderer.domElement.requestPointerLock();
      } else if (gameOptions.newWorld && gameOptions.gameMode) {
        // New world with mode already chosen from MainMenu
        applyGameMode(gameOptions.gameMode);
        startGameLoop();
        renderer.domElement.requestPointerLock();
      } else {
        // Fallback: show game mode selection
        gameModeMenu.show().then((result) => {
          const mode = result.mode || result;
          currentDifficulty = result.difficulty || 'normal';
          applyGameMode(mode);
          startGameLoop();
          renderer.domElement.requestPointerLock();
        });
      }
    }
  }

  // Start loading chunks asynchronously
  loadChunkBatch();

  let _gameLoopStarted = false;
  function startGameLoop() {
    if (_gameLoopStarted) return;
    _gameLoopStarted = true;
    gameLoop.start();
  }

  // --- Terminal ---
  const terminal = new Terminal({
    player, world, inventory, terrainGenerator, hud,
    villaMiseriaPos, villaProsperidadPos,
    dayNight, health, weather, xpSystem,
    potionSystem, entityManager, dimensionManager,
    tileEntities,
  });
  terminal.statsScreen = statsScreen;
  terminal.skinSelector = skinSelector;
  terminal.mazmorra = mazmorra;
  terminal.herrero = herreroScreen;
  terminal.questSystem = questSystem;
  terminal.skillTree = skillTree;
  terminal.bossSystem = bossSystem;
  terminal.playerRef = player;
  terminal.dynamicEvents = dynamicEvents;
  terminal.accessibilityMenu = accessibilityMenu;
  terminal.hudConfig = hudConfig;
  terminal.seasonSystem = seasonSystem;
  terminal.challengeMode = challengeMode;
  terminal.speedrunTimer = speedrunTimer;

  // --- Restore deferred game mode ---
  if (typeof _savedGameMode === 'string' && _savedGameMode) {
    terminal.gameMode = _savedGameMode;
    health.creativeMode = (_savedGameMode === GameMode.CREATIVE);
  }

  // Test hook: expose internals when ?skipIntro=1 (for browser-driven verification)
  if (typeof window !== 'undefined' && window.location && new URLSearchParams(window.location.search).get('skipIntro') === '1') {
    window.__game = { player, world, terrainGenerator, entityManager, bossSystem, terminal, inventory };
  }

  // --- Game Mode ---
  terminal.onGameModeChange = (mode) => {
    eventBus.emit(GameEvents.GAME_MODE_CHANGED, { mode });
  };

  // --- Teleport ---
  let teleportMsg = null;
  let teleportMsgTime = 0;
  let currentDifficulty = Difficulty.NORMAL;

  // --- Shield block callback ---
  health.onShieldBlock = () => {
    soundManager.playPlace(); // metallic click sound for shield
    hud.showMessage('Escudo activo — daño bloqueado', 2);
  };

  // --- Death/Respawn ---
  let daysSurvived = 0;
  // Fall damage: particles + camera shake
  health.onFallDamage = (amount) => {
    particles.spawnBlockBreak(player.position.clone(), 0x8B6B3E); // dirt color
    screenShake.addExplosion(Math.min(1.0, amount * 0.1));
  };
  // Show floating damage number when player takes damage
  health.onDamageNumber = (amount) => {
    damageNumbers.spawn(player.position, amount, 'enemy');
    statsScreen.onDamageTaken(amount);
    comboSystem.onPlayerHit();
    // Damage direction indicator (from last damage source if tracked)
    if (health._lastDamageSourcePos) {
      damageDirection.show(health._lastDamageSourcePos, player.position, fpsCamera.yaw || 0);
    }
  };

  health.onDeath = () => {
    if (challengeMode.active) challengeMode.onDeath();
    // Drop all items at death location
    const deathPos = player.position.clone();
    const allItems = [];
    for (let i = 0; i < inventory.slots.length; i++) {
      if (inventory.slots[i]) { allItems.push(inventory.slots[i]); inventory.slots[i] = null; }
    }
    for (let i = 0; i < inventory.storage.length; i++) {
      if (inventory.storage[i]) { allItems.push(inventory.storage[i]); inventory.storage[i] = null; }
    }
    if (allItems.length > 0) {
      itemDropSystem.dropItems(deathPos, allItems);
    }

    // Show death screen with stats
    const stats = {
      blocksMined: achievements._counters.blocksMined || 0,
      mobsKilled: combat.totalKills || 0,
      daysSurvived: daysSurvived,
      causeOfDeath: health.lastDamageSource || null,
    };
    healthBar.showDeathScreen(() => {
      // Try bed spawn first, then BedSystem spawn, then world spawn
      const bedSpawn = bedSystem.getSpawnPoint() || tileEntities.getBedSpawn();
      let sx, sy, sz;
      if (bedSpawn) {
        sx = bedSpawn.x;
        sy = bedSpawn.y;
        sz = bedSpawn.z;
      } else {
        sx = 0;
        sy = terrainGenerator.getHeight(0, 0) + 2;
        sz = 0;
      }
      player.position.set(sx, sy, sz);
      player.prevPosition.copy(player.position);
      player.velocity.set(0, 0, 0);
      health.respawn();
      xpSystem.onDeath();
      combat.reset();
      healthBar.hideDeathScreen();
      world.updateLoadedChunks(player.position);
      renderer.domElement.requestPointerLock();
    }, stats);
  };

  // --- Check if any UI is open ---
  // --- Mod API init ---
  ModAPI.init(player, world, hud, inventory, health);

  // --- Chat system wiring ---
  chatSystem.onSubmit = (text) => {
    if (text.startsWith('/')) {
      try {
        terminal._execute(text);
        chatSystem.addSystemMessage('Comando ejecutado: ' + text);
      } catch (e) {
        chatSystem.sendMessage('Error: ' + e.message, 'error');
      }
    } else {
      chatSystem.sendMessage(text, 'player');
      // P2P: send chat to remote player
      if (p2pManager.isConnected) {
        p2pManager.sendChat(text);
      }
    }
    ModAPI.fireEvent('onChat', text);
  };

  function isAnyUIOpen() {
    return inventoryScreen.isOpen || terminal.isOpen || craftingScreen._isOpen || chestScreen.isOpen || furnaceScreen.isOpen || tradingScreen.isOpen || enchantScreen.isOpen || brewingScreen.isOpen || recipeBook.isOpen || pauseMenu.isOpen || gameModeMenu.isOpen || multiplayerMenu.isOpen || phoneScreen.isOpen || computerScreen.isOpen || bedSystem.isSleeping || health.dead || chatSystem.isOpen;
  }

  // --- Inventory (E), Terminal (T), Crafting (C), Teleports (V, P) ---
  document.addEventListener('keydown', (e) => {
    if (health.dead) return;
    // Don't handle game keys when terminal, pause menu, or chat is open (except Escape/Enter)
    if ((terminal.isOpen || pauseMenu.isOpen) && e.code !== 'Escape') return;
    if (chatSystem.isOpen && e.code !== 'Escape' && e.code !== 'Enter') return;

    // Enter = Chat
    if (e.code === 'Enter' && !terminal.isOpen && !inventoryScreen.isOpen && !craftingScreen._isOpen) {
      if (chatSystem.isOpen) {
        chatSystem.close();
        renderer.domElement.requestPointerLock();
      } else {
        document.exitPointerLock();
        chatSystem.open();
      }
      return;
    }
    if (e.code === 'KeyE' && !terminal.isOpen && !craftingScreen._isOpen) {
      if (tutorial.isActive()) tutorial.onInventoryOpened();
      inventoryScreen.toggle();
      if (!inventoryScreen.isOpen && document.pointerLockElement !== renderer.domElement) {
        renderer.domElement.requestPointerLock();
      }
    }
    if (e.code === 'KeyC' && !terminal.isOpen && !inventoryScreen.isOpen) {
      craftingScreen.toggle(false); // 2x2 hand crafting
      if (!craftingScreen._isOpen && document.pointerLockElement !== renderer.domElement) {
        renderer.domElement.requestPointerLock();
      }
    }
    if (e.code === 'Escape') {
      if (chatSystem.isOpen) { chatSystem.close(); renderer.domElement.requestPointerLock(); return; }
      if (terminal.isOpen) { terminal.close(); return; }
      if (inventoryScreen.isOpen) { inventoryScreen.close(); return; }
      if (craftingScreen._isOpen) { craftingScreen.close(); return; }
      if (chestScreen.isOpen) { chestScreen.close(); return; }
      if (furnaceScreen.isOpen) { furnaceScreen.close(); return; }
      if (tradingScreen.isOpen) { tradingScreen.close(); return; }
      if (enchantScreen.isOpen) { enchantScreen.close(); return; }
      if (brewingScreen.isOpen) { brewingScreen.close(); return; }
      if (recipeBook.isOpen) { recipeBook.close(); return; }
      if (multiplayerMenu.isOpen) { multiplayerMenu.hide(); return; }
      if (phoneScreen.isOpen) { phoneScreen.close(); renderer.domElement.requestPointerLock(); return; }
      if (computerScreen.isOpen) { computerScreen.close(); renderer.domElement.requestPointerLock(); return; }
      // Toggle pause menu when no other UI is open
      pauseMenu.toggle();
      return;
    }
    // F3 = Performance overlay
    if (e.code === 'F3') {
      e.preventDefault();
      perfOverlay.toggle();
      return;
    }
    // F5 = Toggle camera mode (first person / third person back / third person front)
    if (e.code === 'F5') {
      e.preventDefault();
      fpsCamera.toggleCameraMode();
      return;
    }
    // J = Quest log
    if (e.code === 'KeyJ' && !terminal.isOpen) {
      e.preventDefault();
      if (questSystem.isOpen) questSystem.hideLog();
      else questSystem.showLog();
      return;
    }
    // K = Skill Tree
    if (e.code === 'KeyK' && !terminal.isOpen) {
      e.preventDefault();
      if (skillTree.isOpen) skillTree.hide();
      else skillTree.show();
      return;
    }
    // Spell hotkeys 1-6 (when not in inventory and using Ctrl modifier)
    if (!terminal.isOpen && !inventoryScreen.isOpen && e.ctrlKey && /Digit[1-6]/.test(e.code)) {
      e.preventDefault();
      magicSystem.handleKeyPress(e.code);
      return;
    }
    // M = Minimap
    if (e.code === 'KeyM' && !isAnyUIOpen()) {
      minimap.toggle();
      return;
    }
    // R = Recipe Book
    if (e.code === 'KeyR' && !isAnyUIOpen()) {
      recipeBook.toggle();
      if (recipeBook.isOpen) document.exitPointerLock();
      else if (document.pointerLockElement !== renderer.domElement) renderer.domElement.requestPointerLock();
      return;
    }
    // T = Terminal
    if (e.code === 'KeyT' && !inventoryScreen.isOpen && !craftingScreen._isOpen) {
      terminal.toggle();
      return;
    }
    // V = Teleport to Villa Miseria
    if (e.code === 'KeyV' && !isAnyUIOpen() && !dimensionManager.isInNether()) {
      player.position.set(villaMiseriaPos.x, villaMiseriaPos.y, villaMiseriaPos.z);
      player.prevPosition.copy(player.position);
      player.velocity.set(0, 0, 0);
      world.updateLoadedChunks(player.position);
      teleportMsg = 'Teletransportado a Villa Miseria';
      teleportMsgTime = TELEPORT_MSG_DURATION;
      achievements.onVisitVillaMiseria();
      playBriefVideo('/miseria.webm', 2);
    }
    // N = Toggle map full view
    if (e.code === 'KeyN' && !isAnyUIOpen()) {
      mapItem.toggleFullView();
      return;
    }
    // Y = Teléfono (requires PHONE item or creative mode)
    if (e.code === 'KeyY' && !isAnyUIOpen()) {
      const PHONE_ID = -107;
      const allowed = terminal.gameMode === GameMode.CREATIVE || inventory.hasItem(PHONE_ID);
      if (allowed) {
        document.exitPointerLock();
        phoneScreen.open();
      } else {
        teleportMsg = 'Necesitas un Teléfono para usarlo';
        teleportMsgTime = TELEPORT_MSG_DURATION;
      }
      return;
    }
    // U = Computador (requires COMPUTER item or creative mode)
    if (e.code === 'KeyU' && !isAnyUIOpen()) {
      const COMPUTER_ID = -108;
      const allowed = terminal.gameMode === GameMode.CREATIVE || inventory.hasItem(COMPUTER_ID);
      if (allowed) {
        document.exitPointerLock();
        computerScreen.open();
      } else {
        teleportMsg = 'Necesitas un Computador para usarlo';
        teleportMsgTime = TELEPORT_MSG_DURATION;
      }
      return;
    }
    // P = Teleport to Villa Prosperidad
    if (e.code === 'KeyP' && !isAnyUIOpen() && !dimensionManager.isInNether()) {
      player.position.set(villaProsperidadPos.x, villaProsperidadPos.y, villaProsperidadPos.z);
      player.prevPosition.copy(player.position);
      player.velocity.set(0, 0, 0);
      world.updateLoadedChunks(player.position);
      teleportMsg = 'Teletransportado a Villa Prosperidad';
      teleportMsgTime = TELEPORT_MSG_DURATION;
      achievements.onVisitVillaProspera();
      playBriefVideo('/prosperidad.webm', 2);
    }
    // B = Teleport to Ciudad
    if (e.code === 'KeyB' && !isAnyUIOpen() && !dimensionManager.isInNether()) {
      player.position.set(ciudadPos.x, ciudadPos.y + 4, ciudadPos.z);
      player.prevPosition.copy(player.position);
      player.velocity.set(0, 0, 0);
      world.updateLoadedChunks(player.position);
      teleportMsg = 'Teletransportado a la Ciudad';
      teleportMsgTime = TELEPORT_MSG_DURATION;
    }
  });

  // --- Left click = attack mobs ---
  renderer.domElement.addEventListener('mousedown', (e) => {
    if (e.button !== 0) return;
    if (isAnyUIOpen()) return;
    if (document.pointerLockElement !== renderer.domElement) return;

    // Get all attackable entities (passive + hostile)
    const allEntities = [];
    for (const ent of entityManager.entities) {
      if (ent.type === 'HEROBRINE') continue;
      allEntities.push({
        position: ent.position,
        health: ent.health || 10,
        type: ent.type,
        velocity: ent.velocity,
        mesh: ent.mesh,
        _source: 'passive',
        _ref: ent,
      });
    }
    for (const hm of hostileMobs) {
      allEntities.push({
        position: hm.position,
        health: hm.health,
        type: hm.type,
        velocity: hm.velocity,
        mesh: hm.mesh,
        _source: 'hostile',
        _ref: hm,
      });
    }

    const eyePos = player.position.clone();
    eyePos.y += EYE_HEIGHT;
    const lookDir = new THREE.Vector3();
    camera.getWorldDirection(lookDir);
    const isFalling = !player.onGround && player.velocity.y < -1;

    const result = combat.tryAttack(eyePos, lookDir, allEntities, null, isFalling);
    // Swing sound on every attack attempt (hit or miss)
    if (result) soundManager.playSwing();
    if (result && result.hit) {
      // Game feel: screen shake, impact particles, hit sound
      if (result.critical) {
        screenShake.addCriticalHit();
      } else {
        screenShake.addMeleeHit();
      }
      const hitPos = result.entity.position.clone();
      hitPos.y += 0.9;
      particles.spawnHitImpact(hitPos, result.critical);
      soundManager.playHitImpact();
      // Hunger exhaustion from attacking
      health.onAttack();
      // Tool durability loss on melee hit (swords lose 1, other tools lose 2)
      const equippedSlot = inventory.getSelectedSlot();
      if (equippedSlot && isTool(equippedSlot)) {
        const durResult = damageTool(equippedSlot, equippedSlot.toolType === 'sword' ? 1 : 2);
        if (durResult === null) {
          inventory.slots[inventory.selectedIndex] = null;
          soundManager.playBreak();
        }
        // Weapon XP: increment uses and apply damage multiplier
        const leveledUp = incrementWeaponUses(equippedSlot);
        const mult = getWeaponDamageMult(equippedSlot);
        if (mult > 1.0) {
          result.damage = Math.ceil(result.damage * mult);
        }
        if (leveledUp) {
          hud.showMessage(`¡Arma ascendió a ${getWeaponLevelName(equippedSlot)}!`, 3);
          soundManager.playLevelUp?.();
        }
      }
      // Apply skill tree damage + crit chance
      const sEff = skillTree.getEffects();
      if (sEff.damageMult > 1) result.damage = Math.ceil(result.damage * sEff.damageMult);
      if (sEff.critChance > 0 && Math.random() < sEff.critChance) {
        result.damage = Math.ceil(result.damage * 1.5);
        result.critical = true;
      }
      // Combo system — each hit extends combo
      comboSystem.onHit();
      if (comboSystem.count >= 3) {
        result.damage = Math.ceil(result.damage * comboSystem.getDamageMultiplier());
      }
      // Apply damage to actual entity ref
      const target = result.entity;
      if (target._source === 'passive') {
        const ent = target._ref;
        if (ent._dying) { /* skip dead entities */ }
        else {
        ent.health -= result.damage;
        triggerHurt(ent);
        // Wolf retaliation
        if (ent.type === 'WOLF') entityManager.onWolfAttacked(ent);
        if (ent.health <= 0) {
          triggerDeath(ent);
          eventBus.emit(GameEvents.MOB_KILLED, { type: ent.type, position: ent.position });
          // Mark as dying and delay removal for death animation
          ent._dying = true;
          ent.velocity.set(0, 0, 0);
          const entRef = ent;
          setTimeout(() => {
            const idx = entityManager.entities.indexOf(entRef);
            if (idx >= 0) entityManager._removeEntity(idx);
          }, 1300);
        }
        } // end else (not _dying)
      } else if (target._source === 'hostile') {
        const hm = target._ref;
        hm.health -= result.damage;
        triggerHurt(hm);
        if (hm.health <= 0) {
          eventBus.emit(GameEvents.MOB_KILLED, { type: hm.type, position: hm.position });
          disposeHostileMob(hm);
          const idx = hostileMobs.indexOf(hm);
          if (idx >= 0) hostileMobs.splice(idx, 1);
        }
      }

      // --- Wither boss melee damage ---
      if (bossSystem.wither) {
        const witherDist = eyePos.distanceTo(bossSystem.wither.position);
        if (witherDist < 5) {
          const toWither = bossSystem.wither.position.clone().sub(eyePos).normalize();
          const witherDot = lookDir.dot(toWither);
          if (witherDot > 0.7) {
            const dmg = result.damage || 5;
            bossSystem.damageWither(dmg);
          }
        }
      }

      // --- Wither Storm melee damage (longer reach because of size) ---
      if (bossSystem.witherStorm) {
        const wsDist = eyePos.distanceTo(bossSystem.witherStorm.position);
        if (wsDist < 12) {
          const toWS = bossSystem.witherStorm.position.clone().sub(eyePos).normalize();
          const wsDot = lookDir.dot(toWS);
          if (wsDot > 0.7) {
            const dmg = result.damage || 5;
            bossSystem.damageWitherStorm(dmg);
          }
        }
      }
    }
  });

  // --- Right click = interact with villagers (trading) OR bow charge ---
  renderer.domElement.addEventListener('mousedown', (e) => {
    if (e.button !== 2) return;
    if (isAnyUIOpen()) return;
    if (document.pointerLockElement !== renderer.domElement) return;

    // Check if looking at a villager within 4 blocks
    const eyePos = player.position.clone();
    eyePos.y += EYE_HEIGHT;
    const lookDir = new THREE.Vector3();
    camera.getWorldDirection(lookDir);

    let lookingAtEntity = false;
    for (const ent of entityManager.entities) {
      const toEnt = ent.position.clone().sub(eyePos);
      const dist = toEnt.length();
      if (dist > 4) continue;
      toEnt.normalize();
      const dot = lookDir.dot(toEnt);
      if (dot < 0.85) continue;

      // Villager interaction: quest or trade
      if (ent.type === 'VILLAGER') {
        // 30% chance to offer a quest if player has fewer than 3 active quests
        if (questSystem.activeQuests.length < 3 && !ent._questOffered && Math.random() < 0.3) {
          ent._questOffered = true;
          const quest = questSystem.generateQuestFor(ent);
          document.exitPointerLock();
          questSystem.offerQuest(quest, () => {
            hud.showMessage(`Misión aceptada: ${quest.name}`, 3);
          });
          e.preventDefault();
          e.stopPropagation();
          lookingAtEntity = true;
          return;
        }
        const tradeData = getVillagerTradeData(ent);
        document.exitPointerLock();
        tradingScreen.open(tradeData, () => {});
        playBriefVideo('/trading.webm', 2);
        e.preventDefault();
        e.stopPropagation();
        lookingAtEntity = true;
        return;
      }

      // Wolf taming with bone
      if (ent.type === 'WOLF' && !ent.tamed) {
        const sel = inventory.getSelectedSlot();
        if (sel && sel.itemId === ItemId.BONE) {
          const tamed = entityManager.tryTameWolf(ent);
          sel.count--;
          if (sel.count <= 0) inventory.slots[inventory.selectedIndex] = null;
          if (tamed) {
            teleportMsg = 'Lobo domesticado!';
            teleportMsgTime = TELEPORT_MSG_DURATION;
          }
          lookingAtEntity = true;
          return;
        }
      }

      // Animal breeding with correct food
      if (breedingSystem.canBreed(ent.type)) {
        const sel = inventory.getSelectedSlot();
        if (sel && sel.itemId !== undefined && breedingSystem.tryFeed(ent, sel.itemId)) {
          sel.count--;
          if (sel.count <= 0) inventory.slots[inventory.selectedIndex] = null;
          particles.spawnSmoke(ent.position.clone());
          lookingAtEntity = true;
          return;
        }
      }
    }

    // If not looking at entity, check fishing rod or bow
    if (!lookingAtEntity) {
      const sel = inventory.getSelectedSlot();
      if (sel && FishingSystem.isFishingRod(sel)) {
        const eyeP = player.position.clone();
        eyeP.y += EYE_HEIGHT;
        const lookD = new THREE.Vector3();
        camera.getWorldDirection(lookD);
        fishingSystem.startFishing(eyeP, lookD);
        playBriefVideo('/fishing.webm', 2);
        return;
      }
      if (bowSystem.hasBowSelected()) {
        bowSystem.startCharge();
      }
    }
  });

  // --- Right click release = fire arrow ---
  renderer.domElement.addEventListener('mouseup', (e) => {
    if (e.button !== 2) return;
    if (bowSystem.isCharging) {
      const eyePos = player.position.clone();
      eyePos.y += EYE_HEIGHT;
      const lookDir = new THREE.Vector3();
      camera.getWorldDirection(lookDir);
      bowSystem.releaseCharge(eyePos, lookDir);
    }
  });

  // --- Instructions overlay & pointer lock ---
  // Make instructions clickable so user can click "Haz clic para jugar"
  instructions.style.pointerEvents = 'auto';
  instructions.style.cursor = 'pointer';
  instructions.addEventListener('click', () => {
    renderer.domElement.requestPointerLock();
  });
  renderer.domElement.addEventListener('click', () => {
    if (!document.pointerLockElement && !isAnyUIOpen()) {
      renderer.domElement.requestPointerLock();
    }
  });
  document.addEventListener('pointerlockchange', () => {
    if (document.pointerLockElement === renderer.domElement) {
      instructions.style.display = 'none';
      instructions.style.pointerEvents = 'none';
      soundManager.resume();
    } else {
      if (!isAnyUIOpen()) {
        instructions.style.display = 'block';
        instructions.style.pointerEvents = 'auto';
      }
    }
  });

  // --- FPS counter ---
  let frameCount = 0;
  let fpsTime = 0;
  let currentFps = 0;

  /* La suerte acumulada de lo que Fede lleva encima. Cada bono «Suerte» de
     cualquier objeto del inventario empuja las tiradas de botín hacia arriba:
     es lo que hace que buscar equipo tenga consecuencias. */
  function suerteDelJugador() {
    let total = 0;
    for (const hueco of inventory.slots) if (hueco) total += bonoDe(hueco, 'suerte');
    return total;
  }

  // --- Save helper ---
  function doSave() {
    saveManager.save(
      {
        x: player.position.x,
        y: player.position.y,
        z: player.position.z,
        flying: player.flying,
        health: health.health,
        hunger: health.hunger,
        saturation: health.saturation,
        xp: xpSystem.serialize(),
        potions: Array.from(potionSystem.activeEffects.entries()).map(
          ([id, data]) => ({ id, timeLeft: data.timeLeft, level: data.level })
        ),
        gameMode: terminal.gameMode || GameMode.SURVIVAL,
        dimension: dimensionManager.currentDimension,
        armor: armorSystem.serialize(),
        bed: bedSystem.serialize(),
        redstone: redstoneSystem.serialize(),
        interactiveBlocks: interactiveBlocks.serialize(),
        dayNight: dayNight.serialize(),
        weather: weather.serialize(),
        farming: farmingSystem.serialize(),
      },
      inventory.serialize()
    );
  }

  // --- Hostile Mob Spawning ---
  /** @param {string} [tipoForzado] la mazmorra elige qué invoca; de noche, no. */
  function spawnHostileMob(playerPos, tipoForzado = null) {
    const diffConfig = DIFFICULTY_CONFIG[currentDifficulty] || DIFFICULTY_CONFIG[Difficulty.NORMAL];
    /* La mazmorra se salta los topes de dificultad: si no, en modo fácil no
       llegaría a invocar la oleada entera y la curva se rompería. */
    if (!tipoForzado && !diffConfig.hostileMobs) return;
    if (!tipoForzado && hostileMobs.length >= diffConfig.maxHostileMobs) return;
    const angle = Math.random() * Math.PI * 2;
    const dist = HOSTILE_SPAWN_MIN_DIST + Math.random() * HOSTILE_SPAWN_RANGE;
    const wx = Math.floor(playerPos.x + Math.cos(angle) * dist);
    const wz = Math.floor(playerPos.z + Math.sin(angle) * dist);
    const wy = world.getTopBlock(wx, wz);
    if (wy <= 0) return;
    const surfaceBlock = world.getBlock(wx, wy, wz);
    if (surfaceBlock === BlockId.WATER || surfaceBlock === BlockId.AIR) return;

    // Biome-aware mob selection
    const px = Math.floor(playerPos.x);
    const pz = Math.floor(playerPos.z);
    const biome = terrainGenerator.getBiome ? terrainGenerator.getBiome(px, pz) : 0;
    let types = ['ZOMBIE', 'SKELETON', 'CREEPER', 'SPIDER', 'ENDERMAN', 'SLIME'];
    // Swamp: add Witch and Drowned
    if (biome === 6) types.push('WITCH', 'WITCH', 'DROWNED');
    // Water surface: add Drowned
    if (surfaceBlock === BlockId.WATER) types = ['DROWNED'];
    // Phantom: if player hasn't slept 3+ nights
    if (bedSystem && bedSystem.nightsWithoutSleep >= 3) types.push('PHANTOM', 'PHANTOM');
    // Plains/Forest: occasional Pillager
    if ((biome === 0 || biome === 2) && Math.random() < 0.1) types.push('PILLAGER');

    const type = tipoForzado || types[Math.floor(Math.random() * types.length)];
    const stats = HOSTILE_MOB_TYPES[type];
    if (!stats) return;                    // un tipo inventado no revienta el juego
    if (!stats) return;
    const meshBuilders = {
      ZOMBIE: createZombieMesh, SKELETON: createSkeletonMesh,
      CREEPER: createCreeperMesh, SPIDER: createSpiderMesh,
      ENDERMAN: createEndermanMesh, SLIME: () => createSlimeMesh('large'),
      WITCH: createWitchMesh, PHANTOM: createPhantomMesh,
      DROWNED: createDrownedMesh, PILLAGER: createPillagerMesh,
    };
    const { mesh, refs } = meshBuilders[type]();
    mesh.position.set(wx + 0.5, wy + 1, wz + 0.5);
    scene.add(mesh);

    const mob = {
      type, mesh, refs,
      position: new THREE.Vector3(wx + 0.5, wy + 1, wz + 0.5),
      velocity: new THREE.Vector3(0, 0, 0),
      health: stats.health,
      maxHealth: stats.health,
      // AI state
      aiState: 'idle',
      aiTimer: 0,
      wanderDir: new THREE.Vector3(),
      attackCooldown: 0,
      fuseTimer: 0,
      fusing: false,
      sunBurnTimer: 0,
      idleTimer: 0,
      wanderTimer: 1 + Math.random() * 3,
    };
    if (type === 'SLIME') mob.slimeSize = 'large';
    mob._world = world;
    hostileMobs.push(mob);
  }

  // --- Nether Mob Spawning ---
  function spawnNetherMob(playerPos) {
    const diffConfig = DIFFICULTY_CONFIG[currentDifficulty] || DIFFICULTY_CONFIG[Difficulty.NORMAL];
    if (!diffConfig.hostileMobs) return;
    if (hostileMobs.length >= diffConfig.maxHostileMobs) return;
    const angle = Math.random() * Math.PI * 2;
    const dist = HOSTILE_SPAWN_MIN_DIST + Math.random() * HOSTILE_SPAWN_RANGE;
    const wx = Math.floor(playerPos.x + Math.cos(angle) * dist);
    const wz = Math.floor(playerPos.z + Math.sin(angle) * dist);
    // Find safe spawn Y in nether
    const wy = netherGenerator.findSpawnY(wx, wz);
    if (wy <= 0) return;

    const types = ['ZOMBIE_PIGLIN', 'ZOMBIE_PIGLIN', 'GHAST', 'BLAZE', 'ZOMBIE_PIGLIN'];
    const type = tipoForzado || types[Math.floor(Math.random() * types.length)];
    const stats = HOSTILE_MOB_TYPES[type];
    if (!stats) return;                    // un tipo inventado no revienta el juego
    if (!stats) return;

    const meshBuilders = {
      ZOMBIE_PIGLIN: createZombiePiglinMesh,
      GHAST: createGhastMesh,
      BLAZE: createBlazeMesh,
    };
    const builder = meshBuilders[type];
    if (!builder) return;
    const { mesh, refs } = builder();

    // Ghasts spawn higher
    const spawnY = type === 'GHAST' ? wy + 10 + Math.random() * 15 : wy;
    mesh.position.set(wx + 0.5, spawnY, wz + 0.5);
    scene.add(mesh);

    const mob = {
      type, mesh, refs,
      position: new THREE.Vector3(wx + 0.5, spawnY, wz + 0.5),
      velocity: new THREE.Vector3(0, 0, 0),
      health: stats.health,
      maxHealth: stats.health,
      aiState: 'idle',
      aiTimer: 0,
      wanderDir: new THREE.Vector3(),
      attackCooldown: 0,
      fuseTimer: 0,
      fusing: false,
      sunBurnTimer: 0,
      idleTimer: 0,
      wanderTimer: 1 + Math.random() * 3,
      provoked: false, // For zombie piglin neutral behavior
    };
    hostileMobs.push(mob);
  }

  // --- Game Loop ---
  let wasJumping = false;
  let wasInWater = false;
  let _p2pSyncTimer = 0;

  const gameLoop = new GameLoop(
    (dt) => {
      inputManager.update();

      // Day/night cycle (skip in Nether)
      if (!dimensionManager.isInNether()) {
        dayNight.update(dt, player.position);
      }

      // Weather system (skip in Nether - no weather there)
      const inNether = dimensionManager.isInNether();
      if (!inNether) {
        const playerBiome = terrainGenerator.getBiome(
          Math.floor(player.position.x),
          Math.floor(player.position.z)
        );
        weather.update(dt, player.position, playerBiome);
      }

      // Dimension manager + portal entry
      dimensionManager.update(dt);
      if (dimensionManager.canUsePortal() && checkPortalEntry(player.position, world)) {
        if (dimensionManager.isInNether()) {
          // Return to overworld with smooth transition
          screenEffects.playDimensionTransition('#000000', 1.5, () => {
            const target = dimensionManager.switchToOverworld(player.position);
            world.setGenerator(terrainGenerator);
            world.clearAllChunks();
            const spawnY = terrainGenerator.getHeight(Math.floor(target.x), Math.floor(target.z)) + 2;
            player.position.set(target.x, spawnY, target.z);
            player.prevPosition.copy(player.position);
            player.velocity.set(0, 0, 0);
            world.updateLoadedChunks(player.position);
            scene.fog.color.set(SKY_COLOR);
            scene.background.set(SKY_COLOR);
            scene.fog.near = OVERWORLD_FOG_NEAR;
            scene.fog.far = RENDER_DISTANCE * CHUNK_SIZE * FOG_FAR_FACTOR;
            dayNight.setVisible(true);
            cloudSystem.setVisible(true);
            achievements.onReturnFromNether();
            for (const hm of hostileMobs) disposeHostileMob(hm);
            hostileMobs.length = 0;
            for (const fb of fireballs) { if (fb.mesh) scene.remove(fb.mesh); }
            fireballs.length = 0;
          });
          teleportMsg = 'Regresando al Mundo Principal...';
          teleportMsgTime = TELEPORT_MSG_DURATION;
        } else {
          // Go to Nether with smooth transition
          screenEffects.playDimensionTransition('#330808', 2.0, () => {
            const target = dimensionManager.switchToNether(player.position);
            const spawnY = netherGenerator.findSpawnY(Math.floor(target.x), Math.floor(target.z));
            world.setGenerator(netherGenerator);
            world.clearAllChunks();
            player.position.set(target.x, spawnY, target.z);
            player.prevPosition.copy(player.position);
            player.velocity.set(0, 0, 0);
            world.updateLoadedChunks(player.position);
            scene.fog.color.set(NETHER_SKY_COLOR);
            scene.background.set(NETHER_SKY_COLOR);
            scene.fog.near = NETHER_FOG_NEAR;
            scene.fog.far = NETHER_FOG_FAR;
            dayNight.setVisible(false);
            cloudSystem.setVisible(false);
            achievements.onEnterNether();
            speedrunTimer.mark('nether');
            if (challengeMode.active) challengeMode.markComplete('c20');
            for (const hm of hostileMobs) disposeHostileMob(hm);
            hostileMobs.length = 0;
            buildPortalFrame(world, Math.floor(target.x), spawnY, Math.floor(target.z), 'x');
            world.updateLoadedChunks(player.position);
          }, '/nether.webm');
          teleportMsg = 'Entrando al Nether...';
          teleportMsgTime = TELEPORT_MSG_DURATION;
        }
      }

      // End portal entry check
      if (dimensionManager.canUsePortal() && !dimensionManager.isInNether() && checkEndPortalEntry(player.position, world)) {
        if (dimensionManager.isInEnd()) {
          // Return from End (exit portal after dragon defeat)
          victoryScreen.onComplete = () => {
            screenEffects.playDimensionTransition('#000000', 2.0, () => {
              const target = dimensionManager.switchFromEnd(player.position);
              world.setGenerator(terrainGenerator);
              world.clearAllChunks();
              const spawnY = terrainGenerator.getHeight(Math.floor(target.x), Math.floor(target.z)) + 2;
              player.position.set(target.x, spawnY, target.z);
              player.prevPosition.copy(player.position);
              player.velocity.set(0, 0, 0);
              world.updateLoadedChunks(player.position);
              scene.fog.color.set(SKY_COLOR);
              scene.background.set(SKY_COLOR);
              scene.fog.near = OVERWORLD_FOG_NEAR;
              scene.fog.far = RENDER_DISTANCE * CHUNK_SIZE * FOG_FAR_FACTOR;
              dayNight.setVisible(true);
              cloudSystem.setVisible(true);
              achievements.onReturnFromEnd();
              for (const hm of hostileMobs) disposeHostileMob(hm);
              hostileMobs.length = 0;
            });
          };
          victoryScreen.show({
            timePlayed: Math.floor(performance.now() / 60000),
            blocksMined: achievements._blocksMined || 0,
            mobsKilled: achievements._mobsKilled || 0,
          });
        } else {
          // Enter The End from Overworld
          screenEffects.playDimensionTransition('#0A0012', 2.5, () => {
            const target = dimensionManager.switchToEnd(player.position);
            world.setGenerator(endGenerator);
            world.clearAllChunks();
            const spawnY = endGenerator.findSpawnY(0, 0);
            player.position.set(target.x, spawnY || 52, target.z);
            player.prevPosition.copy(player.position);
            player.velocity.set(0, 0, 0);
            world.updateLoadedChunks(player.position);
            scene.fog.color.set(END_SKY_COLOR);
            scene.background.set(END_SKY_COLOR);
            scene.fog.near = END_FOG_NEAR;
            scene.fog.far = END_FOG_FAR;
            dayNight.setVisible(false);
            cloudSystem.setVisible(false);
            achievements.onEnterEnd();
            speedrunTimer.mark('end');
            for (const hm of hostileMobs) disposeHostileMob(hm);
            hostileMobs.length = 0;
            // Spawn dragon if not already defeated
            if (!dimensionManager.dragonDefeated) {
              bossSystem.spawnDragon(0, 70, 0);
            }
          }, '/dragon.webm');
          teleportMsg = 'Entrando al End...';
          teleportMsgTime = TELEPORT_MSG_DURATION;
        }
      }

      // End ambient effects: void damage below Y=0
      if (dimensionManager.isInEnd() && player.position.y < 0) {
        health.takeDamage(4, 'Ca\u00edda al vac\u00edo');
      }

      // Nether ambient effects
      if (dimensionManager.isInNether()) {
        // Lava damage
        const feetBlock = world.getBlock(
          Math.floor(player.position.x), Math.floor(player.position.y), Math.floor(player.position.z)
        );
        if (feetBlock === BlockId.LAVA && terminal.gameMode !== GameMode.CREATIVE) {
          health.takeDamage(LAVA_DAMAGE, 'Quemado en lava');
        }
        // Keep nether fog
        if (!player.inWater) {
          scene.fog.color.set(NETHER_SKY_COLOR);
          scene.background.set(NETHER_SKY_COLOR);
        }
      }

      // Particle system
      particles.update(dt, player.position);

      // Cloud system (only in overworld)
      cloudSystem.update(dt, player.position);

      // Ambient music + screen effects + cave reverb
      const isUnderground = player.position.y < TERRAIN_BASE && world.getBlock(
        Math.floor(player.position.x), Math.floor(player.position.y) + 4, Math.floor(player.position.z)
      ) !== BlockId.AIR;
      // Cave reverb
      if (soundManager.setCaveMode) {
        soundManager.setCaveMode(isUnderground, Math.max(0, TERRAIN_BASE - player.position.y));
      }
      // Biome-specific music
      if (ambientMusic.setBiome) {
        if (inNether) {
          ambientMusic.setBiome('nether');
        } else {
          const bx = Math.floor(player.position.x);
          const bz = Math.floor(player.position.z);
          const biomeId = terrainGenerator.getBiome(bx, bz);
          const biomeNames = ['plains', 'desert', 'forest', 'tundra', 'mountains', 'jungle', 'swamp', 'savanna'];
          ambientMusic.setBiome(biomeNames[biomeId] || 'plains');
        }
      }
      ambientMusic.update(dt, dayNight.isNight(), player.position.y, isUnderground);

      // Post-processing biome color grading + night
      if (inNether) {
        postProcessing.setBiome('nether');
      } else if (dimensionManager.isInEnd()) {
        postProcessing.setBiome('end');
      } else {
        const _ppBx = Math.floor(player.position.x);
        const _ppBz = Math.floor(player.position.z);
        const _ppBiomeId = terrainGenerator.getBiome(_ppBx, _ppBz);
        const _ppBiomeNames = ['plains', 'desert', 'forest', 'tundra', 'mountains', 'jungle', 'swamp', 'savanna'];
        postProcessing.setBiome(_ppBiomeNames[_ppBiomeId] || 'plains');
      }
      postProcessing.setNight(dayNight.isNight());

      // Ambient soundscape (birds, crickets, wind, drips, lava)
      const px = Math.floor(player.position.x), py = Math.floor(player.position.y), pz = Math.floor(player.position.z);
      const nearLava = world.getBlock(px+1,py,pz) === BlockId.LAVA || world.getBlock(px-1,py,pz) === BlockId.LAVA ||
                       world.getBlock(px,py-1,pz) === BlockId.LAVA || world.getBlock(px,py,pz+1) === BlockId.LAVA ||
                       world.getBlock(px,py,pz-1) === BlockId.LAVA;
      soundManager.updateAmbience(dt, {
        isDay: !dayNight.isNight(),
        isUnderground,
        playerY: player.position.y,
        nearLava,
      });

      screenEffects.update(dt, dayNight.isNight(), isUnderground, potionSystem.hasNightVision(), health.health, player.inWater);

      // Water physics
      waterPhysics.update(dt);

      // Potion system
      potionSystem.update(dt);
      potionEffectDisplay.update();
      // Potion regen
      const regenPerSec = potionSystem.getRegenPerSec();
      if (regenPerSec > 0) health.heal(regenPerSec * dt);
      // Poison damage (can't kill — floors at 1 HP)
      const poisonDmg = potionSystem.getPoisonDamage();
      if (poisonDmg > 0 && health.health > 1) {
        const dmg = poisonDmg * dt;
        if (health.health - dmg < 1) {
          health.health = 1;
        } else {
          health.health -= dmg;
        }
      }
      // Wither damage (CAN kill)
      const witherDmg = potionSystem.getWitherDamage();
      if (witherDmg > 0) {
        health.lastDamageSource = 'Efecto Wither';
        health.health = Math.max(0, health.health - witherDmg * dt);
        if (health.health <= 0 && !health.dead) {
          health.dead = true;
          if (health.onDeath) health.onDeath();
        }
      }
      // Blindness fog override
      const blindFog = potionSystem.getBlindnessFog();
      if (blindFog && !player.inWater) {
        scene.fog.near = blindFog.near;
        scene.fog.far = blindFog.far;
      }
      // Nausea camera wobble
      if (potionSystem.hasNausea()) {
        const wobble = Math.sin(performance.now() * 0.005) * 0.03;
        camera.rotation.z = wobble;
      } else if (camera.rotation.z !== 0) {
        camera.rotation.z = 0;
      }
      // Brewing screen
      if (brewingScreen.isOpen) brewingScreen.updateBrewing(dt);

      // Farming system
      farmingSystem.update(dt);

      // Achievement system
      achievements.update(dt);
      achievements.onPositionUpdate(player.position.y);
      achievements.onHealthUpdate(health.health, health.maxHealth);
      if (player.inWater) achievements.onSwim();
      if (player.flying) achievements.onFly();
      if (dayNight.isNight() && !achievements._nightStarted) achievements.onNightStart();
      if (!dayNight.isNight() && achievements._nightStarted) {
        achievements.onDayStart();
        if (!bedSystem.isSleeping) bedSystem.nightsWithoutSleep++;
      }
      if (weather.isThundering()) achievements.onWeatherStorm();

      // --- Furnace smelting tick ---
      const furnaces = tileEntities.getAllFurnaces();
      for (const f of furnaces) {
        const d = f.data;
        const recipe = craftingSystem.checkFurnaceRecipe(d.input);

        // If fuel is burning, tick it down
        if (d.fuelLeft > 0) {
          d.fuelLeft -= dt / (d.fuelMax || SMELT_DURATION);
          if (d.fuelLeft < 0) d.fuelLeft = 0;

          // If there's a valid recipe and output slot compatible, advance progress
          if (recipe && d.input) {
            const canOutput = !d.output ||
              ((d.output.blockId !== undefined && d.output.blockId === recipe.blockId && d.output.count < MAX_STACK_SIZE) ||
               (d.output.itemId !== undefined && d.output.itemId === recipe.itemId && d.output.count < MAX_STACK_SIZE));

            if (canOutput) {
              d.smeltTime = (d.smeltTime || 0) + dt;
              d.progress = Math.min(d.smeltTime / SMELT_DURATION, 1);

              if (d.smeltTime >= SMELT_DURATION) {
                // Smelting complete
                if (!d.output) {
                  d.output = { ...recipe, count: recipe.count || 1 };
                } else {
                  d.output.count += (recipe.count || 1);
                }
                d.input.count--;
                if (d.input.count <= 0) d.input = null;
                d.smeltTime = 0;
                d.progress = 0;
              }
            } else {
              d.smeltTime = 0;
              d.progress = 0;
            }
          } else {
            d.smeltTime = 0;
            d.progress = 0;
          }
        } else {
          // Try to consume new fuel
          if (recipe && d.fuel && d.input) {
            const fuelVal = getFuelValue(d.fuel);
            if (fuelVal > 0) {
              d.fuelMax = fuelVal * SMELT_DURATION; // convert items-smeltable to seconds
              d.fuelLeft = 1.0;
              d.fuel.count--;
              if (d.fuel.count <= 0) d.fuel = null;
              // Update furnace block to lit
              world.setBlock(f.x, f.y, f.z, BlockId.FURNACE_LIT);
            }
          } else {
            d.smeltTime = 0;
            d.progress = 0;
            // Set furnace block back to unlit
            const currentBlock = world.getBlock(f.x, f.y, f.z);
            if (currentBlock === BlockId.FURNACE_LIT) {
              world.setBlock(f.x, f.y, f.z, BlockId.FURNACE);
            }
          }
        }

        // Update furnace screen if open on this furnace
        if (furnaceScreen.isOpen && furnaceScreen.furnaceData === d) {
          furnaceScreen.updateProgress(d.progress, d.fuelLeft);
        }
      }

      // Don't process player input when UI is open
      if (!isAnyUIOpen()) {
        playerController.update(dt);
        // Soul Sand slowdown
        const feetBlock = world.getBlock(
          Math.floor(player.position.x), Math.floor(player.position.y) - 1, Math.floor(player.position.z)
        );
        if (feetBlock === BlockId.SOUL_SAND && !player.flying) {
          player.velocity.x *= SOUL_SAND_SLOWDOWN;
          player.velocity.z *= SOUL_SAND_SLOWDOWN;
        }
        // Potion speed modifier (boost or slowdown)
        const potionSpeedMult = potionSystem.getSpeedMultiplier();
        if (potionSpeedMult !== 1.0) {
          player.velocity.x *= potionSpeedMult;
          player.velocity.z *= potionSpeedMult;
        }
        // Shield blocking: right mouse + shield equipped
        const selectedItem = inventory.getSelectedSlot();
        const holdingShield = selectedItem && selectedItem.itemId === -103;
        health.shieldActive = holdingShield && inputManager.isMouseDown(2);

        // Horse / vehicle mounting: right-click on tamed horse, car, or moto
        if (!mountedHorse && inputManager.consumeClick && inputManager.consumeClick(2)) {
          for (const ent of entityManager.entities) {
            const canMount = (ent.type === 'HORSE' && ent.tamed) || ent.type === 'CAR' || ent.type === 'MOTO';
            if (!canMount) continue;
            const dx = player.position.x - ent.position.x;
            const dz = player.position.z - ent.position.z;
            if (dx * dx + dz * dz < 9) { // 3 blocks
              mountedHorse = ent;
              ent._isRidden = true;
              if (ent.type === 'CAR') hud.showMessage('Conduciendo auto — Shift para bajar');
              else if (ent.type === 'MOTO') hud.showMessage('Manejando moto — Shift para bajar');
              else hud.showMessage('Montando caballo — Shift para desmontar');
              break;
            }
          }
        }

        // Boat system update
        boatSystem.update(dt, inputManager, player, world, scene, fpsCamera);

        player.update(dt, world);
        blockInteraction.update(dt);
      } else {
        // Cancel bow charge if any UI is open
        bowSystem.cancelCharge();
      }

      // Health system (skip in creative mode)
      if (!player.flying && terminal.gameMode !== GameMode.CREATIVE) {
        health.update(dt, player, world);
      }

      world.updateLoadedChunks(player.position);
      entityManager.update(dt, player.position);
      // Enhanced entity animations
      for (const ent of entityManager.entities) {
        if (ent.type === 'HEROBRINE') continue;
        updateEntityAnimation(ent, dt);
      }
      itemDropManager.update(dt, player.position, inventory, world);
      saveManager.update(dt);
      mazmorra.update(dt);

      // Wolf hostile attacks (wild wolves that were attacked)
      if (terminal.gameMode !== GameMode.CREATIVE) {
        for (const ent of entityManager.entities) {
          if (ent.type !== 'WOLF' || !ent.wolfHostile) continue;
          const wolfResult = entityManager.checkWolfAttack(ent, player.position);
          if (wolfResult.canAttack) {
            const wolfDmg = armorSystem.reduceDamage(wolfResult.damage);
            health.takeDamage(wolfDmg, 'Muerto por Lobo');
            armorSystem.damageArmor(1);
            soundManager.playDamage();
          }
        }
      }

      // Combat update
      const combatResult = combat.update(dt);

      // --- Hostile Mob AI ---
      const isNight = dayNight.isNight();
      hostileSpawnTimer -= dt;
      if ((isNight || inNether) && hostileSpawnTimer <= 0) {
        if (inNether) {
          spawnNetherMob(player.position);
        } else {
          spawnHostileMob(player.position);
        }
        hostileSpawnTimer = HOSTILE_SPAWN_INTERVAL;
      }
      // Despawn hostiles in daylight (except types that don't burn) - only in overworld
      if (!isNight && !inNether) {
        for (let i = hostileMobs.length - 1; i >= 0; i--) {
          const hm = hostileMobs[i];
          const noBurn = hm.type === 'SPIDER' || hm.type === 'ENDERMAN' || hm.type === 'SLIME' || hm.type === 'CREEPER';
          if (!noBurn) {
            hm.sunBurnTimer += dt;
            if (hm.sunBurnTimer > 5) {
              disposeHostileMob(hm);
              hostileMobs.splice(i, 1);
            }
          }
        }
      }

      // Update hostile mob AI
      const _hostileLookDir = new THREE.Vector3();
      camera.getWorldDirection(_hostileLookDir);
      for (let i = hostileMobs.length - 1; i >= 0; i--) {
        const hm = hostileMobs[i];
        const dist = hm.position.distanceTo(player.position);

        // Despawn if too far
        if (dist > HOSTILE_DESPAWN_DISTANCE) {
          disposeHostileMob(hm);
          hostileMobs.splice(i, 1);
          continue;
        }

        const aiResult = updateHostileAI(hm, player.position, dt, world, isNight, _hostileLookDir);
        // Update mesh position
        if (hm.mesh) {
          hm.mesh.position.copy(hm.position);
          // Face direction of movement
          if (hm.velocity.x !== 0 || hm.velocity.z !== 0) {
            hm.mesh.rotation.y = Math.atan2(hm.velocity.x, hm.velocity.z);
          }
        }

        // Hostile mob ambient sounds (random chance)
        if (Math.random() < 0.002) {
          soundManager.playMobSound(hm.type, player.position, hm.position);
        }

        if (aiResult) {
          if (aiResult.type === 'melee') {
            const meleeDmg = armorSystem.reduceDamage(aiResult.damage);
            health.takeDamage(meleeDmg, `Muerto por ${hm.type}`);
            armorSystem.damageArmor(1);
            soundManager.playDamage();
          } else if (aiResult.type === 'arrow') {
            arrows.push(aiResult.arrow);
            scene.add(aiResult.arrow.mesh);
          } else if (aiResult.type === 'explode') {
            const explosion = processCreeperExplosion(
              aiResult.pos, aiResult.radius, aiResult.damage, player.position, world
            );
            soundManager.playExplosion();
            // Screen shake scaled by distance (max 20 blocks)
            const creeperDist = player.position.distanceTo(aiResult.pos);
            screenShake.addExplosion(Math.max(0, 1 - creeperDist / 20));
            if (explosion.playerDamage > 0) {
              const explDmg = armorSystem.reduceDamage(explosion.playerDamage);
              health.takeDamage(explDmg, 'Explosi\u00f3n de Creeper');
              armorSystem.damageArmor(2);
              soundManager.playDamage();
            }
            // Remove creeper
            disposeHostileMob(hm);
            hostileMobs.splice(i, 1);
            // Reload affected chunks
            world.updateLoadedChunks(player.position);
          } else if (aiResult.type === 'split') {
            // Slime splits into smaller slimes
            disposeHostileMob(hm);
            hostileMobs.splice(i, 1);
            const splitCount = aiResult.count || 2;
            for (let s = 0; s < splitCount; s++) {
              const { mesh: sMesh, refs: sRefs } = createSlimeMesh('small');
              const offset = new THREE.Vector3(
                (Math.random() - 0.5) * 2,
                0.5,
                (Math.random() - 0.5) * 2
              );
              const sPos = aiResult.pos.clone().add(offset);
              sMesh.position.copy(sPos);
              scene.add(sMesh);
              hostileMobs.push({
                type: 'SLIME', mesh: sMesh, refs: sRefs,
                position: sPos,
                velocity: new THREE.Vector3(0, 0, 0),
                health: 4, maxHealth: 4,
                slimeSize: 'small',
                aiState: 'idle', aiTimer: 0,
                wanderDir: new THREE.Vector3(),
                attackCooldown: 0, fuseTimer: 0, fusing: false,
                sunBurnTimer: 0, idleTimer: 0,
                wanderTimer: 1 + Math.random() * 2,
              });
            }
          } else if (aiResult.type === 'fireball') {
            fireballs.push(aiResult.fireball);
            scene.add(aiResult.fireball.mesh);
          } else if (aiResult.type === 'death') {
            disposeHostileMob(hm);
            hostileMobs.splice(i, 1);
          }
        }
      }

      // Update fireballs (ghast/blaze)
      for (let i = fireballs.length - 1; i >= 0; i--) {
        const fb = fireballs[i];
        const result = updateFireball(fb, dt, player.position, world);
        if (result) {
          if (result.type === 'hit') {
            if (terminal.gameMode !== GameMode.CREATIVE) {
              const fbDmg = armorSystem.reduceDamage(result.damage);
              health.takeDamage(fbDmg, 'Bola de fuego de Ghast');
              armorSystem.damageArmor(1);
              soundManager.playDamage();
            }
            soundManager.playExplosion();
            screenShake.addExplosion(0.6);
          } else if (result.type === 'stuck') {
            soundManager.playExplosion();
            screenShake.addExplosion(0.4);
          }
          if (fb.mesh) scene.remove(fb.mesh);
          fireballs.splice(i, 1);
        }
      }

      // Update arrows (skeleton arrows)
      for (let i = arrows.length - 1; i >= 0; i--) {
        const arrow = arrows[i];
        const result = updateArrow(arrow, dt, player.position, world);
        if (result) {
          if (result.type === 'hit') {
            const arrowDmg = armorSystem.reduceDamage(result.damage);
            health.takeDamage(arrowDmg, 'Flecha de Esqueleto');
            armorSystem.damageArmor(1);
            soundManager.playDamage();
          }
          scene.remove(arrow.mesh);
          arrows.splice(i, 1);
        }
      }

      // --- Bow system update (player arrows) ---
      bowSystem.update(dt, world);

      // Check player arrow hits against all entities (passive + hostile)
      if (bowSystem.arrows && bowSystem.arrows.length > 0) {
        const allTargets = [];
        for (const ent of entityManager.entities) {
          if (ent.type === 'HEROBRINE') continue;
          allTargets.push({
            position: ent.position,
            health: ent.health || 10,
            type: ent.type,
            velocity: ent.velocity,
            mesh: ent.mesh,
            _source: 'passive',
            _ref: ent,
          });
        }
        for (const hm of hostileMobs) {
          allTargets.push({
            position: hm.position,
            health: hm.health,
            type: hm.type,
            velocity: hm.velocity,
            mesh: hm.mesh,
            _source: 'hostile',
            _ref: hm,
          });
        }

        const arrowHits = bowSystem.checkHits(allTargets);
        for (const hit of arrowHits) {
          const target = hit.entity;
          if (target._source === 'passive') {
            const ent = target._ref;
            ent.health = (ent.health || 10) - hit.damage;
            if (ent.health <= 0) {
              const dropPos = ent.position.clone();
              dropPos.y += 0.5;
              const drops = rollMobDrops(ent.type);
              for (const d of drops) {
                if (d.isBlock) {
                  itemDropManager.dropItem(d.itemId, d.count, dropPos, true);
                } else {
                  itemDropManager.dropNonBlockItem(d.itemId, d.count, dropPos, true);
                }
              }
              const arrowMobXP = MOB_XP[ent.type] || 1;
              particles.spawnXPOrb(dropPos, player.position, arrowMobXP);
              if (xpSystem.addXP(arrowMobXP) > 0) { soundManager.playLevelUp(); particles.spawnLevelUpBurst(player.position); playLevelUpVideo(); }
              achievements.onKillMob(ent.type);
              const idx = entityManager.entities.indexOf(ent);
              if (idx >= 0) {
                entityManager._removeEntity(idx);
              }
            }
          } else if (target._source === 'hostile') {
            const hm = target._ref;
            hm.health -= hit.damage;
            damageNumbers.spawn(hm.position, hit.damage, hit.critical ? 'critical' : 'normal');
            statsScreen.onDamageDealt(hit.damage);
            if (hm.health <= 0) {
              const dropPos = hm.position.clone();
              dropPos.y += 0.5;
              const drops = rollMobDrops(hm.type);
              for (const d of drops) {
                if (d.isBlock) {
                  itemDropManager.dropItem(d.itemId, d.count, dropPos, true);
                } else {
                  itemDropManager.dropNonBlockItem(d.itemId, d.count, dropPos, true);
                }
              }
              const arrowHostileXP = MOB_XP[hm.type] || 5;
              particles.spawnXPOrb(dropPos, player.position, arrowHostileXP);
              if (xpSystem.addXP(arrowHostileXP) > 0) { soundManager.playLevelUp(); particles.spawnLevelUpBurst(player.position); playLevelUpVideo(); }
              achievements.onKillMob(hm.type);
              disposeHostileMob(hm);
              const idx = hostileMobs.indexOf(hm);
              if (idx >= 0) hostileMobs.splice(idx, 1);
            }
          }
        }
      }

      // --- Redstone system update ---
      redstoneSystem.update(dt);
      redstoneSystem.updatePressurePlates(player.position);

      // --- Breeding system update ---
      const breedResult = breedingSystem.update(dt, player.position);
      if (breedResult.bred && breedResult.babyType) {
        // Spawn baby entity
        const baby = entityManager.spawnEntity(
          breedResult.babyType,
          breedResult.babyPos.x, breedResult.babyPos.y, breedResult.babyPos.z
        );
        if (baby) {
          breedingSystem.registerBaby(baby);
        }
        // Give XP for breeding
        if (breedResult.xp > 0) {
          particles.spawnXPOrb(breedResult.babyPos, player.position, breedResult.xp);
          if (xpSystem.addXP(breedResult.xp) > 0) { soundManager.playLevelUp(); particles.spawnLevelUpBurst(player.position); playLevelUpVideo(); }
        }
        playBriefVideo('/breeding.webm', 2);
      }

      // --- Fishing system update ---
      fishingSystem.update(dt);
      // Cancel fishing if player switches away from fishing rod
      if (fishingSystem.isActive()) {
        const sel = inventory.getSelectedSlot();
        if (!sel || !FishingSystem.isFishingRod(sel)) {
          fishingSystem.cancelFishing();
        }
      }

      // --- Boss system update ---
      const bossResult = bossSystem.update(dt, player.position, camera);
      if (bossResult.damage > 0) {
        const dmgAfterArmor = armorSystem.reduceDamage(bossResult.damage);
        health.takeDamage(dmgAfterArmor, 'Muerto por Jefe');
        soundManager.playDamage();
      }
      if (bossResult.explosions.length > 0) {
        world.updateLoadedChunks(player.position);
      }
      // Check for Wither death drops
      const bossDrops = bossSystem.getDeathDrops();
      if (bossDrops) {
        for (const d of bossDrops.items) {
          if (d.isBlock) {
            itemDropManager.dropItem(d.itemId, d.count, bossDrops.position, true);
          } else {
            itemDropManager.dropNonBlockItem(d.itemId, d.count, bossDrops.position, true);
          }
        }
        if (bossDrops.xp > 0) {
          particles.spawnXPOrb(bossDrops.position, player.position, bossDrops.xp);
          if (xpSystem.addXP(bossDrops.xp) > 0) { soundManager.playLevelUp(); particles.spawnLevelUpBurst(player.position); playLevelUpVideo(); }
        }
        // Dragon death: place exit portal, unlock achievement
        if (bossDrops.bossType === 'dragon') {
          dimensionManager.dragonDefeated = true;
          achievements.onDragonDefeat();
          speedrunTimer.mark('dragon');
          if (challengeMode.active) challengeMode.markComplete('c50');
          buildEndExitPortal(world, 0, 48, 0);
          world.updateLoadedChunks(player.position);
          hud.showMessage('¡El Dragón del End ha sido derrotado!');
        }
      }

      // --- Water shader + Cave fog ---
      waterShader.update(dt, camera, player.inWater);
      if (caveFog.setPlayerPosition) caveFog.setPlayerPosition(player.position);
      caveFog.update(dt, player.position.y, isUnderground, dimensionManager.currentDimension);

      // --- Chat system ---
      chatSystem.update(dt);

      // --- P2P Multiplayer sync ---
      if (p2pManager.isConnected) {
        _p2pSyncTimer += dt;
        if (_p2pSyncTimer >= 0.1) {
          _p2pSyncTimer = 0;
          p2pManager.sendPosition(player.position, fpsCamera.yaw || 0);
        }
        // Interpolate remote player mesh toward target position
        if (_remoteVisible && remotePlayerMesh) {
          remotePlayerMesh.position.lerp(_remoteTargetPos, 0.2);
          // Smoothly rotate nametag to face camera
          remotePlayerMesh.rotation.y += (_remoteTargetYaw - remotePlayerMesh.rotation.y) * 0.2;
          // Make nametag always face camera
          const tag = remotePlayerMesh.children[remotePlayerMesh.children.length - 1];
          if (tag) tag.lookAt(camera.position);
        }
      }

      // --- Mod API ---
      ModAPI.update(dt);

      // --- Map system update ---
      mapItem.update(dt, player.position, fpsCamera.yaw || 0, inventory);

      // --- Bed system update ---
      const bedResult = bedSystem.update(dt);
      if (bedResult.skipToDay) {
        dayNight.skipToMorning();
      }

      // --- Grave hide system update ---
      graveHideSystem.update(dt);

      // --- Stats tracking ---
      statsScreen.update(dt, player.position);

      // --- Damage numbers ---
      damageNumbers.update(dt);

      // --- Magic system ---
      magicSystem.update(dt);

      // --- Combo system ---
      comboSystem.update(dt);

      // --- Dynamic events ---
      dynamicEvents.update(dt);

      // --- Damage direction indicator ---
      damageDirection.update(dt);

      // --- Season System ---
      seasonSystem.update(dt, dayNight);

      // --- Challenge Mode ---
      if (challengeMode.active && dayNight.getDay) {
        challengeMode.onDayChange(dayNight.getDay());
      }

      // --- Speedrun Timer ---
      speedrunTimer.update();

      // --- Theme system: update based on biome/dimension/night ---
      if (!themeSystem._lastUpdate || performance.now() - themeSystem._lastUpdate > 2000) {
        themeSystem._lastUpdate = performance.now();
        const dim = dimensionManager.currentDimension;
        const biome = terrainGenerator.getBiome ?
          terrainGenerator.getBiome(Math.floor(player.position.x), Math.floor(player.position.z)) : 0;
        themeSystem.updateFromContext(dim, biome, dayNight.isNight());
      }

      // --- Skill Tree: check for level up, grant points ---
      if (xpSystem.level > skillTree._lastKnownLevel) {
        skillTree.onLevelUp(xpSystem.level);
      }

      // Apply skill tree passive regen
      const skillEffects = skillTree.getEffects();
      if (skillEffects.passiveRegen > 0 && health.health < health.maxHealth) {
        if (!skillTree._regenTimer) skillTree._regenTimer = 0;
        skillTree._regenTimer += dt * skillEffects.passiveRegen;
        if (skillTree._regenTimer >= 1) {
          health.heal(1);
          skillTree._regenTimer -= 1;
        }
      }

      // Apply speed multiplier from skills
      if (skillEffects.speedMult > 1 && !isAnyUIOpen()) {
        player.velocity.x *= skillEffects.speedMult;
        player.velocity.z *= skillEffects.speedMult;
      }

      // --- Bee pollination: pass bee positions to farming system ---
      if (entityManager && farmingSystem) {
        const beePositions = entityManager.entities
          .filter(e => e.type === 'BEE')
          .map(e => e.position);
        farmingSystem.setBeePositions(beePositions);
      }

      // --- Cat creeper scare: creepers flee from tamed cats ---
      if (entityManager && hostileMobs) {
        const tamedCats = entityManager.entities.filter(e => e.type === 'CAT' && e.tamed);
        for (const hm of hostileMobs) {
          if (hm.type !== 'CREEPER') continue;
          for (const cat of tamedCats) {
            const dx = hm.position.x - cat.position.x;
            const dz = hm.position.z - cat.position.z;
            if (dx * dx + dz * dz < 64) { // 8 blocks radius
              // Push creeper away from cat
              const dist = Math.sqrt(dx * dx + dz * dz) || 1;
              hm.position.x += (dx / dist) * dt * 3;
              hm.position.z += (dz / dist) * dt * 3;
            }
          }
        }
      }

      // --- Iron Golem AI: attack nearby hostile mobs ---
      if (entityManager && hostileMobs) {
        for (const ent of entityManager.entities) {
          if (ent.type !== 'IRON_GOLEM') continue;
          if (!ent._golemCooldown) ent._golemCooldown = 0;
          ent._golemCooldown -= dt;
          for (const hm of hostileMobs) {
            const dx = hm.position.x - ent.position.x;
            const dz = hm.position.z - ent.position.z;
            const distSq = dx * dx + dz * dz;
            if (distSq < 256 && ent._golemCooldown <= 0) { // 16 blocks
              // Move toward hostile
              const dist = Math.sqrt(distSq) || 1;
              ent.position.x += (dx / dist) * ent.speed * dt * 0.5;
              ent.position.z += (dz / dist) * ent.speed * dt * 0.5;
              if (ent.mesh) ent.mesh.position.copy(ent.position);
              // Attack if close enough
              if (distSq < 9) { // 3 blocks
                hm.health -= 10;
                ent._golemCooldown = 1.5;
              }
            }
          }
        }
      }

      // --- Horse / Vehicle mounting ---
      if (mountedHorse) {
        const isCar = mountedHorse.type === 'CAR';
        const isMoto = mountedHorse.type === 'MOTO';
        // For vehicles, the player drives them: vehicle follows player horizontally
        if (isCar || isMoto) {
          mountedHorse.position.x = player.position.x;
          mountedHorse.position.z = player.position.z;
          mountedHorse.position.y = player.position.y - 1.0;
          if (mountedHorse.mesh) {
            mountedHorse.mesh.position.copy(mountedHorse.position);
            // Face camera yaw
            mountedHorse.mesh.rotation.y = fpsCamera.yaw + Math.PI;
          }
        } else {
          // Horse: player follows horse
          player.position.copy(mountedHorse.position);
          player.position.y += 1.5;
          if (mountedHorse.mesh) mountedHorse.mesh.position.copy(mountedHorse.position);
        }
        // Dismount with Shift
        if (inputManager.isKeyDown('ShiftLeft') || inputManager.isKeyDown('ShiftRight')) {
          if (mountedHorse._isRidden !== undefined) mountedHorse._isRidden = false;
          mountedHorse = null;
          player.flying = false;
        }
      }

      // --- Hopper system update ---
      hopperSystem.update(dt);

      // --- Raid system update ---
      raidSystem.update(dt);
      raidSystem.checkTrigger(player.position, [villaMiseriaPos, villaProsperidadPos]);

      // --- Item drop system update (death drops pickup) ---
      const pickedUp = itemDropSystem.update(dt, player.position);
      if (pickedUp && pickedUp.length > 0) {
        for (const item of pickedUp) {
          if (item.toolId) {
            inventory.addTool(item);
          } else if (item.itemId !== undefined) {
            inventory.addItem(item.itemId, item.count || 1);
          } else if (item.blockId !== undefined) {
            for (let c = 0; c < (item.count || 1); c++) inventory.addBlock(item.blockId);
          }
          // Pickup sparkle
          particles.spawnPickupSparkle(player.position);
        }
      }

      // --- Day counter for death screen stats ---
      if (!dayNight.isNight() && dayNight._wasNight) daysSurvived++;
      dayNight._wasNight = dayNight.isNight();

      // --- Hunger exhaustion: sprint, swim, jump ---
      if (player.inWater && !player.flying) health.onSwim(dt);
      if (health.sprinting) health.onSprint(dt);

      // Sound: jump
      if (player.velocity.y > 0 && !wasJumping && player.onGround === false && !player.flying && !player.inWater) {
        soundManager.playJump();
        health.onJump();
      }
      wasJumping = player.velocity.y > 0;

      // Sound: splash
      if (player.inWater && !wasInWater) {
        soundManager.playSplash();
      }
      wasInWater = player.inWater;

      // Tutorial: detect player movement
      if (tutorial.isActive() && playerController.isWalking()) tutorial.onPlayerMove();

      // Sound: footsteps (with surface material detection)
      if (!isAnyUIOpen()) {
        const feetBlockId = world.getBlock(
          Math.floor(player.position.x),
          Math.floor(player.position.y) - 1,
          Math.floor(player.position.z)
        );
        soundManager.updateFootsteps(dt, playerController.isWalking(), player.onGround, feetBlockId);
        // Running dust particles (only when sprinting on ground)
        if (playerController.isWalking() && player.onGround && feetBlockId > 0) {
          if (!player._dustTimer) player._dustTimer = 0;
          player._dustTimer -= dt;
          if (player._dustTimer <= 0) {
            player._dustTimer = 0.15; // every 0.15s
            if (playerController.sprinting) {
              particles.spawnSprintDust(player.position, 0x8B7355);
            } else {
              particles.spawnRunDust(player.position, 0x8B7355);
            }
          }
        }
      }

      // Hotbar & Health bar update
      hotbar.update();
      healthBar.update();
      // XP bar update
      if (healthBar.setXP) {
        healthBar.setXP(xpSystem.getProgress(), xpSystem.level);
      }
    },
    (alpha) => {
      player.interpolateCamera(alpha, camera);

      // Third-person camera offset (must be after interpolateCamera sets eye position)
      if (fpsCamera.cameraMode !== 0) {
        fpsCamera.updateThirdPerson(camera.position.clone());
      }

      /* El cuerpo, colocado y animado.

         Antes el muñeco de tercera persona ni se movía ni se giraba: se quedaba
         plantado mirando siempre al norte mientras la cámara daba vueltas. La
         fase del paso avanza con la distancia recorrida y no con el tiempo, así
         que al andar despacio las piernas van despacio. */
      if (tpGroup && tpGroup.visible) {
        tpGroup.position.set(
          player.prevPosition.x + (player.position.x - player.prevPosition.x) * alpha,
          player.prevPosition.y + (player.position.y - player.prevPosition.y) * alpha,
          player.prevPosition.z + (player.position.z - player.prevPosition.z) * alpha,
        );
        tpGroup.rotation.y = fpsCamera.yaw + Math.PI;
        const vx = player.velocity.x, vz = player.velocity.z;
        const rapidez = Math.hypot(vx, vz);
        _pasoFase += rapidez * 0.055;
        animarModelo(tpGroup, {
          fase: _pasoFase,
          andando: rapidez > 0.6 && player.onGround,
          enElAire: !player.onGround && !player.flying,
        });
        // la cabeza mira arriba y abajo con la cámara
        tpGroup.userData.partes.cabeza.rotation.x = -fpsCamera.pitch;
      }

      /* El mapa de sombras viaja con el jugador. Si se quedara fijo en el
         origen, a veinte bloques de casa ya no habría sombras. */
      if (directionalLight.castShadow) {
        directionalLight.target.position.set(player.position.x, player.position.y, player.position.z);
        /* Sol bajo, a unos 35°. A 60° las sombras salen cortas y pegadas al
           bloque, y no se distingue nada; bajarlo las alarga y de golpe se ve
           qué está encima de qué. */
        directionalLight.position.set(
          player.position.x + 130, player.position.y + 95, player.position.z + 65);
        directionalLight.target.updateMatrixWorld();
      }

      // Weather screen shake (tiny camera offset during thunder)
      const shake = weather.getShakeOffset();
      if (shake.lengthSq() > 0) {
        camera.position.add(shake);
      }

      // Combat/explosion screen shake
      const combatShake = screenShake.update(TICK_DT);
      if (combatShake.lengthSq() > 0) {
        camera.position.add(combatShake);
      }

      // FOV: sprint boost + combat punch
      const sprintFovTarget = playerController.sprinting ? SPRINT_FOV_BOOST : 0;
      // Zoom: hold C to zoom in (FOV 30)
      const zoomActive = inputManager.isKeyDown('KeyC');
      let targetFov = zoomActive ? 30 : (CAMERA_FOV + sprintFovTarget);
      if (combat.isPunching) {
        const punchResult = combat.update(0);
        targetFov += punchResult.fovOffset;
      }
      if (Math.abs(camera.fov - targetFov) > 0.1) {
        camera.fov += (targetFov - camera.fov) * Math.min(1, dt * 8);
        camera.updateProjectionMatrix();
      }

      // Underwater fog - coordinate with day/night
      if (player.inWater) {
        dayNight.setUnderwaterOverride(true);
        scene.fog.near = UNDERWATER_FOG_NEAR;
        scene.fog.far = UNDERWATER_FOG_FAR;
        scene.fog.color.set(UNDERWATER_COLOR);
        scene.background.set(UNDERWATER_COLOR);
      } else {
        dayNight.setUnderwaterOverride(false);
      }

      // Frustum culling
      world.updateFrustum();

      // Minimap
      minimap.update(TICK_DT, player.position, fpsCamera.yaw || 0, [...entityManager.entities, ...hostileMobs]);

      if (composer) {
        composer.render();
      } else {
        if (postWebGPU && brilloActivo) postWebGPU.renderAsync();
        else renderer.render(scene, camera);
      }

      // FPS counter
      frameCount++;
      fpsTime += TICK_DT;
      if (fpsTime >= 1) {
        currentFps = frameCount;
        frameCount = 0;
        fpsTime = 0;
      }
      // Teleport message
      if (teleportMsgTime > 0) {
        teleportMsgTime -= TICK_DT;
        if (teleportMsg && teleportMsgTime > 0) {
          hud.showMessage(teleportMsg);
          if (teleportMsgTime <= 0) teleportMsg = null;
        }
      }
      hud.update(player, currentFps, TICK_DT, dimensionManager.getDimensionName());

      // Performance overlay (F3)
      perfOverlay.update(TICK_DT, {
        renderer,
        entityCount: entityManager.entities.length,
        hostileCount: hostileMobs.length,
        chunkCount: world.getLoadedChunkCount ? world.getLoadedChunkCount() : '?',
        playerPos: player.position,
        dimension: dimensionManager.getDimensionName(),
        usingWebGPU,
      });
    }
  );

  // --- Handle Resize ---
  window.addEventListener('resize', () => {
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(window.innerWidth, window.innerHeight);
    if (composer) composer.setSize(window.innerWidth, window.innerHeight);
  });

  // --- Wire SaveManager auto-save data providers ---
  saveManager._getPlayerData = () => ({
    x: player.position.x,
    y: player.position.y,
    z: player.position.z,
    flying: player.flying,
    health: health.health,
    hunger: health.hunger,
    saturation: health.saturation,
    xp: xpSystem.serialize(),
    potions: Array.from(potionSystem.activeEffects.entries()).map(
      ([id, data]) => ({ id, timeLeft: data.timeLeft, level: data.level })
    ),
    gameMode: terminal.gameMode || GameMode.SURVIVAL,
    difficulty: currentDifficulty,
    seed: gameOptions.seed || '',
    dimension: dimensionManager.currentDimension,
    armor: armorSystem.serialize(),
    bed: bedSystem.serialize(),
    redstone: redstoneSystem.serialize(),
    interactiveBlocks: interactiveBlocks.serialize(),
    dayNight: dayNight.serialize(),
    weather: weather.serialize(),
    farming: farmingSystem.serialize(),
  });
  saveManager._getInventoryData = () => inventory.serialize();
  saveManager.onSaveError = (msg) => {
    if (hud) hud.showMessage(msg, 5);
  };
  saveManager.onSaveSuccess = () => {
    notifications.system('💾 Partida guardada');
  };

  // --- Auto-save on page unload ---
  window.addEventListener('beforeunload', () => {
    doSave();
  });

  // --- Periodic auto-save every 30s (loses less progress on crash) ---
  setInterval(() => {
    try { doSave(); } catch (e) { console.warn('autosave failed:', e); }
  }, 30000);

  // --- Save on tab hidden (mobile / tab switch protection) ---
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      try { doSave(); } catch {}
    }
  });

}
